import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import axios from 'axios'
import { toast } from 'react-toastify'
import { PulseLoader } from 'react-spinners'
import * as XLSX from 'xlsx'
import {
  resolveFileContentType,
} from '../utils/s3Upload'

const API_URL = process.env.REACT_APP_API_URL

/** Resolve base64 xlsx string from export API (direct, API Gateway body, or nested JSON). */
function extractExcelBase64(raw) {
  if (raw == null) return null
  if (typeof raw === 'string') {
    const t = raw.trim()
    if (t.startsWith('UEs')) return t
    if (t.startsWith('{') || t.startsWith('[')) {
      try {
        return extractExcelBase64(JSON.parse(t))
      } catch {
        return null
      }
    }
    // data URL: data:application/...;base64,UEs...
    const comma = t.indexOf(',')
    if (t.includes('base64') && comma > 0) {
      return extractExcelBase64(t.slice(comma + 1))
    }
    return null
  }
  if (typeof raw === 'object') {
    if (raw.body != null) return extractExcelBase64(raw.body)
    if (typeof raw.data === 'string') return extractExcelBase64(raw.data)
    if (raw.data != null && typeof raw.data === 'object') {
      const nested = extractExcelBase64(raw.data)
      if (nested) return nested
    }
    if (raw.file != null) return extractExcelBase64(raw.file)
    if (raw.fileBase64 != null) return extractExcelBase64(raw.fileBase64)
    if (raw.base64 != null) return extractExcelBase64(raw.base64)
    if (raw.excel != null) return extractExcelBase64(raw.excel)
  }
  return null
}

function downloadXlsxFromBase64(base64, fileName) {
  const clean = String(base64 || '').trim()
  if (!clean.startsWith('UEs')) {
    throw new Error('Invalid Excel data received')
  }
  const byteCharacters = atob(clean)
  const byteNumbers = new Array(byteCharacters.length)
  for (let i = 0; i < byteCharacters.length; i += 1) {
    byteNumbers[i] = byteCharacters.charCodeAt(i)
  }
  const byteArray = new Uint8Array(byteNumbers)
  const blob = new Blob([byteArray], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
  })
  const url = window.URL.createObjectURL(blob)
  const link = document.createElement('a')
  link.href = url
  link.download = fileName || `rfq-supplier-quote-${Date.now()}.xlsx`
  document.body.appendChild(link)
  link.click()
  document.body.removeChild(link)
  window.URL.revokeObjectURL(url)
}

function normalizeExcelHeader(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/[%#]/g, '')
    .replace(/[^a-z0-9]+/g, '')
}

function normalizeExcelKey(value) {
  return String(value || '')
    .trim()
    .toLowerCase()
    .replace(/\s+/g, '')
}

function excelCellString(value) {
  if (value == null) return ''
  return String(value).trim()
}

/** Excel export uses "-" for non-editable / blank cells — treat as empty. */
function isBlankExcelValue(value) {
  const v = excelCellString(value)
  if (!v) return true
  const lower = v.toLowerCase()
  return (
    v === '-' ||
    v === '--' ||
    v === '—' ||
    v === '–' ||
    lower === 'n/a' ||
    lower === 'na' ||
    lower === 'null' ||
    lower === 'undefined'
  )
}

function pickExcelField(row, aliases) {
  if (!row || typeof row !== 'object') return ''
  const wanted = aliases.map(normalizeExcelHeader)
  for (const [key, value] of Object.entries(row)) {
    if (wanted.includes(normalizeExcelHeader(key))) {
      const text = excelCellString(value)
      return isBlankExcelValue(text) ? '' : text
    }
  }
  return ''
}

const EXCEL_FIELD_ALIASES = {
  lineKey: ['linekey', 'line', 'lineno', 'sno', 's.no', 'key', 'rowkey'],
  materialCode: ['materialcode', 'code', 'itemcode'],
  materialDescription: [
    'materialdescription',
    'description',
    'material',
    'itemdescription'
  ],
  hsn: ['hsn', 'hsncode'],
  basicPrice: [
    'basicprice',
    'price',
    'unitprice',
    'quotedprice',
    'basic',
    'rate'
  ],
  gstPercent: ['gst', 'gstpercent', 'gst%', 'tax', 'taxpercent'],
  technicalResponse: [
    'technicalresponse',
    'technicalremarks',
    'technicalremark',
    'comments',
    'response',
    'techresponse',
    'technicalcomments'
  ],
  boqNo: ['boqno', 'boqid', 'boq', 'boqline'],
  rfqTerm: ['rfqterm', 'term', 'terms', 'termname'],
  quoteResponse: [
    'quoteresponse',
    'termscomments',
    'supplierresponse',
    'termsresponse',
    'comments',
    'response'
  ]
}

/**
 * Build lookup of LineKey / boqNo / material code → materialPrices path.
 * Supports export keys like "1", "1.1", "M0", "M0-B0".
 */
function buildExcelLineTargetMap(materialsList) {
  const map = new Map()
  const setKey = (key, target) => {
    const normalized = normalizeExcelKey(key)
    if (!normalized || map.has(normalized)) return
    map.set(normalized, target)
  }

  ;(Array.isArray(materialsList) ? materialsList : []).forEach((material, index) => {
    const lineNo =
      material?.lineNo != null && material.lineNo !== ''
        ? String(material.lineNo)
        : String(index + 1)
    setKey(lineNo, { type: 'material', index })
    setKey(`M${index}`, { type: 'material', index })
    setKey(`MAT${index + 1}`, { type: 'material', index })
    setKey(`L${lineNo}`, { type: 'material', index })

    const code = String(material?.materialCode || '').trim()
    if (code && code !== '-') {
      setKey(`CODE:${code}`, { type: 'material', index })
    }

    const boqItems = getMaterialBoqItems(material)
    boqItems.forEach((boqItem, boqIndex) => {
      const boqNo = String(
        boqItem?.boqNo || `${lineNo}.${boqIndex + 1}`
      ).trim()
      setKey(boqNo, { type: 'boq', index, boqIndex })
      setKey(`${lineNo}.${boqIndex + 1}`, { type: 'boq', index, boqIndex })
      setKey(`M${index}-B${boqIndex}`, { type: 'boq', index, boqIndex })
      setKey(`M${index}.B${boqIndex}`, { type: 'boq', index, boqIndex })
      setKey(`B${boqNo}`, { type: 'boq', index, boqIndex })

      const boqCode = String(boqItem?.materialCode || '').trim()
      if (boqCode && boqCode !== '-') {
        setKey(`BOQCODE:${boqCode}`, { type: 'boq', index, boqIndex })
      }
    })
  })

  return map
}

function resolveExcelRowTarget(row, lineMap, materialsList, rowIndex) {
  const lineKey = pickExcelField(row, EXCEL_FIELD_ALIASES.lineKey)
  if (lineKey) {
    const byKey = lineMap.get(normalizeExcelKey(lineKey))
    if (byKey) return byKey
  }

  const boqNo = pickExcelField(row, EXCEL_FIELD_ALIASES.boqNo)
  if (boqNo) {
    const byBoq = lineMap.get(normalizeExcelKey(boqNo))
    if (byBoq) return byBoq
  }

  const code = pickExcelField(row, EXCEL_FIELD_ALIASES.materialCode)
  if (code && code !== '-') {
    const byBoqCode = lineMap.get(normalizeExcelKey(`BOQCODE:${code}`))
    if (byBoqCode) return byBoqCode
    const byCode = lineMap.get(normalizeExcelKey(`CODE:${code}`))
    if (byCode) return byCode
  }

  // Fallback: sequential non-empty data rows → materials / boq flattened order
  const flat = []
  ;(Array.isArray(materialsList) ? materialsList : []).forEach((material, index) => {
    flat.push({ type: 'material', index })
    getMaterialBoqItems(material).forEach((_, boqIndex) => {
      flat.push({ type: 'boq', index, boqIndex })
    })
  })
  return flat[rowIndex] || null
}

function workbookSheetsToRowGroups(workbook) {
  const sheetNames = workbook.SheetNames || []
  const groups = { materials: [], terms: [] }

  sheetNames.forEach((name) => {
    const sheet = workbook.Sheets[name]
    if (!sheet) return
    const rows = XLSX.utils.sheet_to_json(sheet, {
      defval: '',
      raw: false,
      blankrows: false
    })
    if (!Array.isArray(rows) || rows.length === 0) return

    const sheetKey = normalizeExcelHeader(name)
    const sample = rows[0] || {}
    const headers = Object.keys(sample).map(normalizeExcelHeader)
    const looksLikeTerms =
      sheetKey.includes('term') ||
      headers.some((h) =>
        ['rfqterm', 'term', 'terms', 'quoteresponse', 'termscomments'].includes(h)
      )
    const looksLikeMaterials =
      sheetKey.includes('material') ||
      sheetKey.includes('quote') ||
      sheetKey.includes('boq') ||
      headers.some((h) =>
        [
          'linekey',
          'materialcode',
          'materialdescription',
          'basicprice',
          'hsn',
          'gst'
        ].includes(h)
      )

    if (looksLikeTerms && !looksLikeMaterials) {
      groups.terms.push(...rows)
    } else if (looksLikeMaterials || groups.materials.length === 0) {
      groups.materials.push(...rows)
    } else if (looksLikeTerms) {
      groups.terms.push(...rows)
    }
  })

  // If only one sheet and it wasn't classified as terms, use it as materials.
  if (groups.materials.length === 0 && sheetNames.length > 0) {
    const first = XLSX.utils.sheet_to_json(workbook.Sheets[sheetNames[0]], {
      defval: '',
      raw: false,
      blankrows: false
    })
    groups.materials = Array.isArray(first) ? first : []
  }

  return groups
}

async function parseSupplierQuoteExcelFile(file) {
  const buffer = await file.arrayBuffer()
  const workbook = XLSX.read(buffer, { type: 'array' })
  return workbookSheetsToRowGroups(workbook)
}

function parseApiResponse(raw) {
  let d = raw
  if (typeof d === 'string') {
    try {
      d = JSON.parse(d)
    } catch {
      return raw
    }
  }
  if (d?.body != null) {
    if (typeof d.body === 'string') {
      try {
        return JSON.parse(d.body)
      } catch {
        return d.body
      }
    }
    return d.body
  }
  return d
}

function parseJsonIfString(value) {
  if (typeof value !== 'string') return value
  try {
    return JSON.parse(value)
  } catch {
    return value
  }
}

function normalizeApiPayload(raw) {
  let obj = raw
  if (!obj || typeof obj !== 'object') return raw
  obj = { ...obj }
  if (typeof obj.data === 'string') {
    obj.data = parseJsonIfString(obj.data)
  }
  return obj
}

function isApiSuccess(responseData) {
  if (!responseData || typeof responseData !== 'object') return false
  const s = responseData.success
  return s === true || s === 'true' || s === 1 || s === '1'
}

function pickRfqFromResponse(responseData) {
  let dataBlock = responseData?.data != null ? responseData.data : responseData
  if (dataBlock == null) return null
  if (typeof dataBlock === 'string') {
    dataBlock = parseJsonIfString(dataBlock)
  }
  if (!dataBlock || typeof dataBlock !== 'object') return null

  if (dataBlock.rfq && typeof dataBlock.rfq === 'object') {
    return dataBlock.rfq
  }

  if (
    !Array.isArray(dataBlock) &&
    dataBlock.rfqs == null &&
    (dataBlock._id != null || dataBlock.rfqNumber != null || dataBlock.rfqNo != null)
  ) {
    return dataBlock
  }

  return null
}

function formatDate(value) {
  if (!value) return '-'
  const str = String(value).trim()
  if (/^\d{2}-\d{2}-\d{4}$/.test(str)) return str
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return str || '-'
  return d.toLocaleDateString()
}

function formatDueDateTime(value) {
  if (!value) return '-'
  const str = String(value).trim()
  if (/^\d{2}-\d{2}-\d{4}[ T]\d{2}:\d{2}/.test(str)) {
    return str.replace('T', ' ')
  }
  if (/^\d{2}-\d{2}-\d{4}$/.test(str)) return str
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return str || '-'
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  })
}

function parseRfqDueDate(value) {
  if (!value) return null
  const str = String(value).trim()

  if (/^\d{2}-\d{2}-\d{4}$/.test(str)) {
    const [dd, mm, yyyy] = str.split('-')
    return new Date(Number(yyyy), Number(mm) - 1, Number(dd), 23, 59, 59, 999)
  }

  if (/^\d{2}-\d{2}-\d{4}[ T]\d{2}:\d{2}/.test(str)) {
    const [datePart, timePart] = str.split(/[ T]/)
    const [dd, mm, yyyy] = datePart.split('-')
    const [hh, min] = timePart.split(':')
    return new Date(
      Number(yyyy),
      Number(mm) - 1,
      Number(dd),
      Number(hh),
      Number(min),
      59,
      999
    )
  }

  const d = new Date(value)
  return Number.isNaN(d.getTime()) ? null : d
}

function isRfqDueDatePassed(dueDate) {
  const due = parseRfqDueDate(dueDate)
  if (!due) return false
  return Date.now() > due.getTime()
}

function isDecimalInput(value) {
  if (value === '' || value == null) return true
  // Allow thousand separators (commas) while typing/pasting, e.g. 23,580 or 23,580.00
  const normalized = String(value).trim().replace(/,/g, '')
  return /^\d*\.?\d*$/.test(normalized)
}

function normalizeDecimalInput(value) {
  if (value == null) return ''
  return String(value).trim().replace(/,/g, '').replace(/\s/g, '')
}

function parseDecimalValue(value) {
  const normalized = normalizeDecimalInput(value)
  if (normalized === '' || normalized === '.') return NaN
  return parseFloat(normalized)
}

function formatAmount(value) {
  const num = Number(value)
  if (!Number.isFinite(num)) return '0.00'
  return num.toFixed(2)
}

function roundMoney(value) {
  const num = Number(value)
  if (!Number.isFinite(num)) return NaN
  return Math.round(num * 100) / 100
}

function positiveTargetNumber(...values) {
  for (const value of values) {
    if (value == null || value === '') continue
    const num = Number(value)
    if (Number.isFinite(num) && num > 0) return num
  }
  return NaN
}

/** Effective line/material target: supplier-specific → targetPrice → all-suppliers. */
function getBoqEffectiveTargetPrice(boqItem) {
  return positiveTargetNumber(
    boqItem?.supplierTargetPrice,
    boqItem?.targetPrice,
    boqItem?.allSuppliersTargetPrice
  )
}

function getMaterialEffectiveTargetPrice(material) {
  return positiveTargetNumber(
    material?.supplierTargetPrice,
    material?.targetPrice,
    material?.allSuppliersTargetPrice
  )
}

function getRfqEffectiveTargetPrice(rfq) {
  return positiveTargetNumber(
    rfq?.targetPrice,
    rfq?.allSuppliersTargetPrice?.targetPrice,
    rfq?.supplierTargetPrice?.targetPrice
  )
}

/**
 * Only show a line target when the supplier's quoted unit price is still above it.
 * If they already quoted at/below the target, no need to list that target.
 */
function shouldShowPriceMatchTarget(quotedUnitPrice, targetPrice) {
  const target = Number(targetPrice)
  if (!Number.isFinite(target) || target <= 0) return false
  const quoted = Number(quotedUnitPrice)
  if (!Number.isFinite(quoted)) return true
  return quoted > target
}

function hasRfqTargetPrice(rfq) {
  if (Number.isFinite(getRfqEffectiveTargetPrice(rfq))) return true

  const materials = Array.isArray(rfq?.materials) ? rfq.materials : []
  for (const material of materials) {
    if (Number.isFinite(getMaterialEffectiveTargetPrice(material))) return true
    const boqItems = getMaterialBoqItems(material)
    for (const boqItem of boqItems) {
      if (Number.isFinite(getBoqEffectiveTargetPrice(boqItem))) return true
    }
  }

  const supplierMaterials = Array.isArray(rfq?.supplierTargetPrice?.materials)
    ? rfq.supplierTargetPrice.materials
    : []
  for (const material of supplierMaterials) {
    if (Number.isFinite(getMaterialEffectiveTargetPrice(material))) return true
    const boqItems = Array.isArray(material?.boq)
      ? material.boq
      : Array.isArray(material?.boqItems)
        ? material.boqItems
        : []
    for (const boqItem of boqItems) {
      if (Number.isFinite(getBoqEffectiveTargetPrice(boqItem))) return true
    }
  }

  return false
}

function getSupplierPreviousQuote(rfq, supplierQuote) {
  if (supplierQuote?.previousQuote && typeof supplierQuote.previousQuote === 'object') {
    return supplierQuote.previousQuote
  }
  if (rfq?.previousQuote && typeof rfq.previousQuote === 'object') {
    return rfq.previousQuote
  }
  if (rfq?.supplier?.previousQuote && typeof rfq.supplier.previousQuote === 'object') {
    return rfq.supplier.previousQuote
  }
  return null
}

function getSupplierQuoteAfterTarget(rfq, supplierQuote) {
  if (
    supplierQuote?.quoteAfterTargetPrice &&
    typeof supplierQuote.quoteAfterTargetPrice === 'object'
  ) {
    return supplierQuote.quoteAfterTargetPrice
  }
  if (
    rfq?.quoteAfterTargetPrice &&
    typeof rfq.quoteAfterTargetPrice === 'object'
  ) {
    return rfq.quoteAfterTargetPrice
  }
  if (
    rfq?.supplier?.quoteAfterTargetPrice &&
    typeof rfq.supplier.quoteAfterTargetPrice === 'object'
  ) {
    return rfq.supplier.quoteAfterTargetPrice
  }
  return null
}

function getQuoteHistoryList(rfq, supplierQuote) {
  if (Array.isArray(supplierQuote?.quoteHistory) && supplierQuote.quoteHistory.length) {
    return supplierQuote.quoteHistory
  }
  if (Array.isArray(rfq?.quoteHistory) && rfq.quoteHistory.length) {
    return rfq.quoteHistory
  }
  if (Array.isArray(rfq?.supplier?.quoteHistory) && rfq.supplier.quoteHistory.length) {
    return rfq.supplier.quoteHistory
  }
  return []
}

/**
 * Max Total Value for price-matching updates:
 * previousQuote (before target) — never the later after-target revision.
 * Returns NaN when there is no real before-matching quote yet.
 */
function getPreviousQuoteMaxTotal(rfq, supplierQuote) {
  const previous = getSupplierPreviousQuote(rfq, supplierQuote)
  const fromPrevious = Number(previous?.grossTotalValue)
  if (Number.isFinite(fromPrevious) && fromPrevious > 0) return fromPrevious

  const history = getQuoteHistoryList(rfq, supplierQuote)
  const beforeTarget = history.find(
    (row) =>
      String(row?.phase || '').toLowerCase() === 'before_target_price' ||
      Number(row?.revisionNo) === 1
  )
  const fromHistory = Number(beforeTarget?.grossTotalValue)
  if (Number.isFinite(fromHistory) && fromHistory > 0) return fromHistory

  // Live gross only when this supplier already has a submitted quote (> 0)
  if (hasQuoteSubmitted(rfq, supplierQuote)) {
    const fromGross = Number(rfq?.grossTotal?.grossTotalValue)
    if (Number.isFinite(fromGross) && fromGross > 0) return fromGross

    if (supplierQuote && Number.isFinite(Number(supplierQuote.grossTotalValue))) {
      const fromSupplier = Number(supplierQuote.grossTotalValue)
      if (fromSupplier > 0) return fromSupplier
    }

    if (Array.isArray(rfq?.materials) && rfq.materials.length > 0) {
      const fromMaterials = rfq.materials.reduce(
        (sum, material) => sum + (Number(material?.totalValue) || 0),
        0
      )
      if (fromMaterials > 0) return fromMaterials
    }
  }

  return NaN
}

function extractSupplierFromQuoteUpdateResponse(responseData, supplierMailId, supplierId) {
  const data = responseData?.data
  if (!data || typeof data !== 'object') return null

  if (data.supplier && typeof data.supplier === 'object') {
    return data.supplier
  }

  const suppliers = Array.isArray(data.suppliers) ? data.suppliers : []
  const idKey = String(supplierId || '').trim()
  if (idKey) {
    const byId = suppliers.find((s) => getSupplierRecordId(s) === idKey)
    if (byId) return byId
  }

  const mail = String(supplierMailId || '').trim().toLowerCase()
  if (mail) {
    const byMail = suppliers.find(
      (s) => String(s?.supplierMailId || '').trim().toLowerCase() === mail
    )
    if (byMail) return byMail
  }

  return suppliers[0] || null
}

function getMaterialBoqItems(material) {
  if (Array.isArray(material?.boqItems) && material.boqItems.length > 0) {
    return material.boqItems
  }
  if (material?.isBoq && Array.isArray(material?.boq) && material.boq.length > 0) {
    return material.boq
  }
  return []
}

function materialHasBoq(material) {
  return getMaterialBoqItems(material).length > 0
}

function getBoqItemId(item) {
  return String(item?.boq_id || item?._id || item?.id || '').trim()
}

/** Parent kit basic = Σ (child basicPrice × child quantity) */
function calculateBoqKitBasicPrice(boqItems, boqPrices) {
  return (boqItems || []).reduce((sum, boqItem, boqIndex) => {
    const unitPrice =
      parseDecimalValue(boqPrices?.[boqIndex]?.basicPrice) || 0
    const qty = parseFloat(boqItem?.quantity) || 0
    return sum + unitPrice * qty
  }, 0)
}

function calculateMaterialPricing(material, priceRow) {
  const quantity = parseFloat(material?.quantity) || 0
  const gstPercent = parseDecimalValue(priceRow?.gstPercent) || 0
  const boqItems = getMaterialBoqItems(material)
  const hasBoq = boqItems.length > 0

  // BOQ parent: basic price is rolled up from children (not entered on parent)
  const basicPrice = hasBoq
    ? calculateBoqKitBasicPrice(boqItems, priceRow?.boqPrices)
    : parseDecimalValue(priceRow?.basicPrice) || 0

  const lineBasic = basicPrice * quantity
  const gstValue = (lineBasic * gstPercent) / 100
  const totalValue = lineBasic + gstValue
  return {
    quantity,
    basicPrice,
    gstPercent,
    lineBasic,
    gstValue,
    totalValue,
    hasBoq
  }
}

function decodeMailParam(value) {
  if (!value) return ''
  try {
    return decodeURIComponent(value).trim()
  } catch {
    return String(value).trim()
  }
}

const MAX_SUPPLIER_ATTACHMENTS = 5
const SUPPLIER_ALLOWED_EXTENSIONS = ['jpeg', 'jpg', 'png', 'pdf', 'xlsx', 'ppt']

function mapAttachmentFromApi(item) {
  return {
    s3Key: item?.s3Key || '',
    originalFileName: item?.originalFileName || '',
    contentType: item?.contentType || '',
    uploadedAt: item?.uploadedAt || '',
    url: item?.url || ''
  }
}

function mapRfqOwnerFromApi(item) {
  return mapAttachmentFromApi(item)
}

function getSupplierAttachmentsFromRfq(rfq, supplierQuote) {
  if (Array.isArray(rfq?.attachments) && rfq.attachments.length > 0) {
    return rfq.attachments.map(mapAttachmentFromApi).filter((att) => att.s3Key)
  }
  if (Array.isArray(supplierQuote?.attachments) && supplierQuote.attachments.length > 0) {
    return supplierQuote.attachments.map(mapAttachmentFromApi).filter((att) => att.s3Key)
  }
  return []
}

function isSupplierFileAllowed(file) {
  const ext = getFileExtension(file?.name, file?.type)
  return SUPPLIER_ALLOWED_EXTENSIONS.includes(ext)
}

function formatAllowedFormatsList() {
  return SUPPLIER_ALLOWED_EXTENSIONS.map((ext) => ext.toUpperCase()).join(', ')
}

function getAttachmentLabel(att) {
  if (!att) return 'Attachment'
  if (att.originalFileName) return att.originalFileName
  const key = att.s3Key || ''
  if (!key) return 'Attachment'
  const parts = String(key).split('/')
  return parts[parts.length - 1] || key
}

function getFileExtension(fileName, contentType) {
  const name = String(fileName || '').trim()
  const dotExt = name.includes('.') ? name.split('.').pop().toLowerCase() : ''
  if (dotExt && dotExt.length <= 6) return dotExt

  const typeMap = {
    'application/pdf': 'pdf',
    'image/jpeg': 'jpg',
    'image/png': 'png',
    'image/gif': 'gif',
    'image/webp': 'webp',
    'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx',
    'application/vnd.ms-excel': 'xls',
    'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
    'application/msword': 'doc',
    'text/plain': 'txt',
    'application/zip': 'zip'
  }
  return typeMap[String(contentType || '').toLowerCase()] || ''
}

function getFileTypeMeta(extension, contentType) {
  const ext = String(extension || getFileExtension('', contentType)).toLowerCase()

  if (ext === 'pdf') {
    return { icon: 'fa-file-pdf', color: '#dc3545', bg: '#fde8ea', label: 'PDF' }
  }
  if (ext === 'xlsx' || ext === 'xls' || ext === 'csv') {
    return { icon: 'fa-file-excel', color: '#198754', bg: '#e8f5ee', label: ext.toUpperCase() || 'EXCEL' }
  }
  if (ext === 'docx' || ext === 'doc') {
    return { icon: 'fa-file-word', color: '#0d6efd', bg: '#e7f1ff', label: ext.toUpperCase() || 'WORD' }
  }
  if (['jpg', 'jpeg', 'png', 'gif', 'webp', 'bmp', 'svg'].includes(ext)) {
    return { icon: 'fa-file-image', color: '#6f42c1', bg: '#f3ebff', label: ext.toUpperCase() || 'IMAGE' }
  }
  if (ext === 'zip' || ext === 'rar' || ext === '7z') {
    return { icon: 'fa-file-zipper', color: '#fd7e14', bg: '#fff3e6', label: ext.toUpperCase() || 'ZIP' }
  }
  if (ext === 'ppt' || ext === 'pptx') {
    return { icon: 'fa-file-powerpoint', color: '#d63384', bg: '#fce8f3', label: ext.toUpperCase() || 'PPT' }
  }
  if (ext === 'txt') {
    return { icon: 'fa-file-lines', color: '#6c757d', bg: '#f1f3f5', label: 'TXT' }
  }
  return {
    icon: 'fa-file',
    color: '#495057',
    bg: '#f8f9fa',
    label: ext ? ext.toUpperCase() : 'FILE'
  }
}

function truncateFileName(name, maxLength = 28) {
  const str = String(name || '').trim()
  if (str.length <= maxLength) return str
  const ext = str.includes('.') ? `.${str.split('.').pop()}` : ''
  const baseMax = Math.max(maxLength - ext.length - 1, 8)
  return `${str.slice(0, baseMax)}…${ext}`
}

function formatAttachmentDate(value) {
  if (!value) return ''
  const d = new Date(value)
  if (Number.isNaN(d.getTime())) return ''
  return d.toLocaleString('en-IN', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: true
  })
}

function getSupplierMailIdFromSearch(searchParams) {
  const mail =
    searchParams.get('supplierMailId') ||
    searchParams.get('email') ||
    searchParams.get('supplierEmail') ||
    ''
  return decodeMailParam(mail)
}

function resolveSupplierMailId(supplierMailIdParam, searchParams) {
  if (supplierMailIdParam) {
    return decodeMailParam(supplierMailIdParam)
  }
  return getSupplierMailIdFromSearch(searchParams)
}

function getSupplierRecordId(supplier) {
  return String(
    supplier?.supplierId ||
      supplier?._id ||
      supplier?.id ||
      ''
  ).trim()
}

function findSupplierQuote(rfq, supplierMailId, supplierId = '') {
  if (!rfq) return null

  const suppliers = Array.isArray(rfq.suppliers) ? rfq.suppliers : []
  const idKey = String(supplierId || '').trim()

  if (idKey) {
    if (rfq.supplier && typeof rfq.supplier === 'object') {
      const nestedId = getSupplierRecordId(rfq.supplier)
      if (nestedId && nestedId === idKey) {
        return rfq.supplier
      }
    }
    const byId = suppliers.find((s) => getSupplierRecordId(s) === idKey)
    if (byId) return byId
  }

  if (!supplierMailId) return null
  const mail = supplierMailId.trim().toLowerCase()

  if (rfq.supplier && typeof rfq.supplier === 'object') {
    const supplierMail = String(rfq.supplier.supplierMailId || rfq.supplierMailId || '')
      .trim()
      .toLowerCase()
    if (!supplierMail || supplierMail === mail) {
      return rfq.supplier
    }
  }

  const matched = suppliers.find(
    (s) => String(s.supplierMailId || '').trim().toLowerCase() === mail
  )
  if (matched) return matched

  const legacyMail = String(rfq.supplierMailId || '').trim().toLowerCase()
  if (legacyMail && legacyMail === mail) {
    return {
      supplierName: rfq.supplierName || '',
      supplierMailId: rfq.supplierMailId,
      materialQuotes: rfq.materialQuotes,
      terms: rfq.supplierTerms
    }
  }

  return null
}

function getMaterialPrId(item) {
  return String(item?.pr_id || item?.prId || '').trim()
}

function materialMatchesQuote(material, quote) {
  const prIdM = getMaterialPrId(material)
  const prIdQ = getMaterialPrId(quote)
  if (prIdM && prIdQ) return prIdM === prIdQ

  const codeM = String(material?.materialCode || '').trim()
  const codeQ = String(quote?.materialCode || '').trim()
  if (codeM && codeQ && codeM === codeQ && codeM !== '-') return true

  const descM = String(material?.materialDescription || '').trim()
  const descQ = String(quote?.materialDescription || '').trim()
  return Boolean(descM && descQ && descM === descQ)
}

function findMaterialQuote(material, index, quotes) {
  if (!Array.isArray(quotes) || quotes.length === 0) return null

  const prId = getMaterialPrId(material)
  if (prId) {
    const byPrId = quotes.find((q) => getMaterialPrId(q) === prId)
    if (byPrId) return byPrId
  }

  if (quotes[index] && materialMatchesQuote(material, quotes[index])) {
    return quotes[index]
  }

  const byCode = String(material?.materialCode || '').trim()
  if (byCode && byCode !== '-') {
    const matched = quotes.find((q) => String(q.materialCode || '').trim() === byCode)
    if (matched) return matched
  }

  return quotes.find((q) => materialMatchesQuote(material, q)) || quotes[index] || null
}

function supplierHasSubmittedQuote(supplier) {
  if (!supplier) return false
  if (supplier.quoteSubmittedAt) return true

  const quotes = Array.isArray(supplier.materialQuotes) ? supplier.materialQuotes : []
  return quotes.some(
    (quote) =>
      quote?.basicPrice != null &&
      quote.basicPrice !== '' &&
      Number.isFinite(Number(quote.basicPrice))
  )
}

function normalizeQuoteResponse(value) {
  const str = String(value ?? '').trim()
  return str === '-' ? '' : str
}

/** Keep saved quote/technical responses as-is for edit fields (including "-", "---"). */
function toEditableComment(value) {
  if (value == null) return ''
  return String(value).trim()
}

function hasQuoteSubmitted(rfq, supplierQuote) {
  if (rfq?.quoteSubmitted === true || rfq?.quoteSubmitted === 'true') return true
  if (rfq?.quoteSubmittedAt) return true
  return supplierHasSubmittedQuote(supplierQuote)
}

function getGrossTotalsFromRfq(rfq, supplierQuote, calculated) {
  const gross = rfq?.grossTotal
  if (gross && typeof gross === 'object') {
    return {
      grossBasicPrice: Number(gross.grossTotalBasicPrice) || calculated.grossBasicPrice,
      grossGstValue: Number(gross.grossGstValue) || calculated.grossGstValue,
      grossTotalValue: Number(gross.grossTotalValue) || calculated.grossTotalValue
    }
  }

  if (supplierQuote) {
    return {
      grossBasicPrice:
        Number(supplierQuote.grossTotalBasicPrice) || calculated.grossBasicPrice,
      grossGstValue: Number(supplierQuote.grossGstValue) || calculated.grossGstValue,
      grossTotalValue: Number(supplierQuote.grossTotalValue) || calculated.grossTotalValue
    }
  }

  return calculated
}

function materialHasQuoteValues(material) {
  return (
    material?.basicPrice != null &&
    material.basicPrice !== '' &&
    Number.isFinite(Number(material.basicPrice))
  )
}

function findBoqQuotePrice(boqItem, boqIndex, existingQuote) {
  const quoteBoq =
    (Array.isArray(existingQuote?.boqItems) && existingQuote.boqItems) ||
    (Array.isArray(existingQuote?.boq) && existingQuote.boq) ||
    []
  if (quoteBoq.length === 0) {
    if (boqItem?.basicPrice != null && boqItem.basicPrice !== '') {
      return String(boqItem.basicPrice)
    }
    return ''
  }

  const boqId = getBoqItemId(boqItem)
  if (boqId) {
    const byId = quoteBoq.find((q) => getBoqItemId(q) === boqId)
    if (byId?.basicPrice != null && byId.basicPrice !== '') {
      return String(byId.basicPrice)
    }
  }

  const byNo = quoteBoq.find(
    (q) =>
      String(q.boqNo || '').trim() === String(boqItem?.boqNo || '').trim() &&
      String(q.materialCode || '').trim() ===
        String(boqItem?.materialCode || '').trim()
  )
  if (byNo?.basicPrice != null && byNo.basicPrice !== '') {
    return String(byNo.basicPrice)
  }

  if (
    quoteBoq[boqIndex]?.basicPrice != null &&
    quoteBoq[boqIndex].basicPrice !== ''
  ) {
    return String(quoteBoq[boqIndex].basicPrice)
  }

  if (boqItem?.basicPrice != null && boqItem.basicPrice !== '') {
    return String(boqItem.basicPrice)
  }
  return ''
}

function buildInitialMaterialPrices(materials, rfq, supplierQuote) {
  const quotes = Array.isArray(supplierQuote?.materialQuotes)
    ? supplierQuote.materialQuotes
    : []

  return materials.map((m, index) => {
    const existing = findMaterialQuote(m, index, quotes)
    const boqItems = getMaterialBoqItems(m)
    const hasBoq = boqItems.length > 0
    let basicPrice = ''

    if (!hasBoq) {
      if (existing?.basicPrice != null && existing.basicPrice !== '') {
        basicPrice = String(existing.basicPrice)
      } else if (materialHasQuoteValues(m)) {
        basicPrice = String(m.basicPrice)
      } else if (m.basicPrice != null && m.basicPrice !== '') {
        basicPrice = String(m.basicPrice)
      }
    }

    return {
      hsn:
        existing?.hsn ||
        existing?.hsnCode ||
        m.hsn ||
        m.hsnCode ||
        '',
      basicPrice,
      boqPrices: boqItems.map((boqItem, boqIndex) => ({
        basicPrice: findBoqQuotePrice(boqItem, boqIndex, existing)
      })),
      gstPercent:
        existing?.gstPercent != null && existing.gstPercent !== ''
          ? String(existing.gstPercent)
          : m.gstPercent != null && m.gstPercent !== ''
            ? String(m.gstPercent)
            : m.gst != null && m.gst !== ''
              ? String(m.gst)
              : '',
      comments: toEditableComment(
        existing?.comments ||
          existing?.technicalResponse ||
          m.technicalResponse ||
          m.comments ||
          ''
      )
    }
  })
}

function buildInitialSupplierTerms(rfq, supplierQuote) {
  const terms = Array.isArray(rfq?.terms) ? rfq.terms : []
  const savedTerms = supplierQuote?.terms || supplierQuote?.termQuotes || []
  const quoteMap = new Map()
  if (Array.isArray(savedTerms)) {
    savedTerms.forEach((t) => {
      const key = String(t.rfqTerm || '').trim()
      if (key) quoteMap.set(key, t)
    })
  }

  if (terms.length > 0) {
    return terms.map((t) => {
      const key = String(t.rfqTerm || '').trim()
      const existing = key ? quoteMap.get(key) : null
      return {
        sno: t.sno,
        rfqTerm: t.rfqTerm || '',
        termsResponse: t.termsResponse || '',
        comments: toEditableComment(
          t.quoteResponse ||
            t.comments ||
            t.termsComments ||
            existing?.comments ||
            existing?.quoteResponse ||
            existing?.termsComments ||
            ''
        )
      }
    })
  }

  const baseRows = getTermsRows(rfq).map((row) => ({
    rfqTerm: row.rfqTerm === '-' ? '' : row.rfqTerm,
    termsResponse: row.termsResponse === '-' ? '' : row.termsResponse,
    comments: ''
  }))

  return baseRows.map((row) => {
    const key = String(row.rfqTerm || '').trim()
    const existing = key ? quoteMap.get(key) : null
    return {
      ...row,
      comments: toEditableComment(
        existing?.comments ||
          existing?.quoteResponse ||
          existing?.termsComments ||
          ''
      )
    }
  })
}

function getTermsRows(rfq) {
  if (Array.isArray(rfq?.terms) && rfq.terms.length > 0) {
    return rfq.terms.map((t) => ({
      sno: t.sno,
      rfqTerm: t.rfqTerm || '-',
      termsResponse: t.termsResponse || '-',
      quoteResponse: normalizeQuoteResponse(
        t.quoteResponse || t.comments || t.termsComments || ''
      ) || '-'
    }))
  }
  if (rfq?.rfqTerms) {
    return [{ rfqTerm: rfq.rfqTerms, termsResponse: rfq.termsResponse || '-' }]
  }
  return []
}

function getMaterialDisplayValues(material, priceRow, calculated, isEditing) {
  if (isEditing) {
    return {
      // BOQ parent: show rolled-up kit basic; otherwise editable unit price
      basicPrice: materialHasBoq(material)
        ? calculated.basicPrice
        : priceRow.basicPrice,
      gstPercent: priceRow.gstPercent,
      gstValue: calculated.gstValue,
      totalValue: calculated.totalValue,
      technicalResponse: toEditableComment(
        priceRow.comments || material.technicalResponse || material.comments || ''
      )
    }
  }

  return {
    basicPrice: materialHasBoq(material)
      ? calculated.basicPrice || material.basicPrice || priceRow.basicPrice
      : material.basicPrice ?? priceRow.basicPrice,
    gstPercent: material.gstPercent ?? priceRow.gstPercent,
    gstValue: material.gstValue ?? calculated.gstValue,
    totalValue: material.totalValue ?? calculated.totalValue,
    technicalResponse: toEditableComment(
      material.technicalResponse || material.comments || priceRow.comments || ''
    ) || '-'
  }
}

function emptySubmitErrors() {
  return { formMessage: '', terms: {}, materials: {} }
}

function validateQuoteSubmission(materialPrices, supplierTerms, materials) {
  const errors = emptySubmitErrors()
  let hasErrors = false

  if (materials.length === 0) {
    errors.formMessage = 'No materials available to submit.'
    return { hasErrors: true, errors }
  }

  materialPrices.forEach((row, index) => {
    const fieldErrors = {}
    const material = materials[index]
    const boqItems = getMaterialBoqItems(material)
    const hasBoq = boqItems.length > 0

    if (!String(row.hsn || '').trim()) {
      fieldErrors.hsn = 'HSN is required.'
      hasErrors = true
    }

    if (hasBoq) {
      const boqErrors = {}
      boqItems.forEach((boqItem, boqIndex) => {
        const boqPrice = row.boqPrices?.[boqIndex]?.basicPrice
        if (!String(boqPrice || '').trim()) {
          boqErrors[boqIndex] = 'Basic Price is required.'
          hasErrors = true
        } else if (!Number.isFinite(parseDecimalValue(boqPrice))) {
          boqErrors[boqIndex] = 'Enter a valid Basic Price.'
          hasErrors = true
        }
      })
      if (Object.keys(boqErrors).length > 0) {
        fieldErrors.boqPrices = boqErrors
      }
    } else if (!String(row.basicPrice || '').trim()) {
      fieldErrors.basicPrice = 'Basic Price is required.'
      hasErrors = true
    } else if (!Number.isFinite(parseDecimalValue(row.basicPrice))) {
      fieldErrors.basicPrice = 'Enter a valid Basic Price.'
      hasErrors = true
    }

    if (!String(row.gstPercent || '').trim()) {
      fieldErrors.gstPercent = 'GST % is required.'
      hasErrors = true
    } else if (!Number.isFinite(parseDecimalValue(row.gstPercent))) {
      fieldErrors.gstPercent = 'Enter a valid GST %.'
      hasErrors = true
    }

    if (!String(row.comments || '').trim()) {
      fieldErrors.comments = 'Technical Response is required.'
      hasErrors = true
    }

    if (Object.keys(fieldErrors).length > 0) {
      errors.materials[index] = fieldErrors
    }
  })

  supplierTerms.forEach((row, index) => {
    if (!String(row.comments || '').trim()) {
      errors.terms[index] = 'Quote Response is required.'
      hasErrors = true
    }
  })

  if (hasErrors && !errors.formMessage) {
    errors.formMessage = 'Please complete all mandatory fields before submitting.'
  }

  return { hasErrors, errors }
}

const ViewField = ({ label, children }) => (
  <div className="form-group row align-items-center">
    <label className="col-12 col-md-4 col-form-label">{label}</label>
    <div className="col-12 col-md-8">
      <div className="txtdsply">{children}</div>
    </div>
  </div>
)

const SectionTitle = ({ icon, title, actions = null }) => (
  <div className="row mb-2 mt-3 align-items-center">
    <div className={actions ? 'col-12 col-md' : 'col-12'}>
      <h5 className="text-primary border-bottom pb-2 mb-3 mb-md-0">
        <i className={`fa-solid ${icon} mr-2`} />
        {title}
      </h5>
    </div>
    {actions ? (
      <div className="col-12 col-md-auto mb-3 mb-md-0 text-md-right">
        {actions}
      </div>
    ) : null}
  </div>
)

const AttachmentGrid = ({
  attachments = [],
  pendingFiles = [],
  onDownload,
  downloadingS3Key,
  isDownloadDisabled = false,
  onRemove,
  onRemovePending,
  emptyMessage = 'No attachments',
  emptyIcon = 'fa-regular fa-folder-open'
}) => {
  const totalCount = attachments.length + pendingFiles.length

  if (totalCount === 0) {
    return (
      <div
        className="text-center text-muted mb-3 py-4"
        style={{
          border: '1px dashed #dee2e6',
          borderRadius: '8px',
          fontSize: '13px'
        }}
      >
        <i
          className={`${emptyIcon} mb-2`}
          style={{ fontSize: '28px', display: 'block', opacity: 0.6 }}
        />
        {emptyMessage}
      </div>
    )
  }

  const renderCardShell = (key, fileMeta, fileName, subtitle, footer) => (
    <div
      key={key}
      style={{
        border: '1px solid #dee2e6',
        borderRadius: '10px',
        background: '#fff',
        padding: '14px',
        display: 'flex',
        flexDirection: 'column',
        minHeight: onRemove || onRemovePending ? '188px' : '168px',
        boxShadow: '0 1px 2px rgba(0,0,0,0.04)'
      }}
    >
      <div className="d-flex align-items-start mb-2" style={{ gap: '10px' }}>
        <div
          style={{
            width: '44px',
            height: '44px',
            borderRadius: '10px',
            background: fileMeta.bg,
            color: fileMeta.color,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
            fontSize: '20px'
          }}
        >
          <i className={`fa-solid ${fileMeta.icon}`} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <span
            className="badge"
            style={{
              backgroundColor: fileMeta.bg,
              color: fileMeta.color,
              fontSize: '10px',
              fontWeight: 700,
              letterSpacing: '0.03em'
            }}
          >
            {fileMeta.label}
          </span>
          <div
            title={fileName}
            style={{
              fontSize: '13px',
              fontWeight: 600,
              lineHeight: 1.35,
              marginTop: '6px',
              wordBreak: 'break-word'
            }}
          >
            {truncateFileName(fileName, 32)}
          </div>
        </div>
      </div>

      {subtitle ? (
        <div className="text-muted mb-3" style={{ fontSize: '11px', lineHeight: 1.4 }}>
          {subtitle}
        </div>
      ) : (
        <div className="mb-3" />
      )}

      <div className="mt-auto">{footer}</div>
    </div>
  )

  return (
    <div
      className="mb-3"
      style={{
        display: 'grid',
        gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
        gap: '12px'
      }}
    >
      {attachments.map((att) => {
        const fileName = getAttachmentLabel(att)
        const extension = getFileExtension(fileName, att.contentType)
        const fileMeta = getFileTypeMeta(extension, att.contentType)
        const isDownloading = downloadingS3Key === att.s3Key

        return renderCardShell(
          att.s3Key || fileName,
          fileMeta,
          fileName,
          att.uploadedAt ? (
            <>
              <i className="fa-regular fa-clock mr-1" />
              {formatAttachmentDate(att.uploadedAt)}
            </>
          ) : null,
          <div className="d-flex" style={{ gap: '8px' }}>
            <button
              type="button"
              className="btn btn-sm btn-blue"
              onClick={() => onDownload(att.s3Key)}
              disabled={isDownloadDisabled}
              style={{ flex: 1 }}
            >
              {isDownloading ? (
                <>
                  <span
                    className="spinner-border spinner-border-sm mr-1"
                    role="status"
                    aria-hidden="true"
                  />
                  Downloading...
                </>
              ) : (
                <>
                  <i className="fa-solid fa-download mr-1" />
                  Download
                </>
              )}
            </button>
            {onRemove ? (
              <button
                type="button"
                className="btn btn-sm btn-outline-danger"
                onClick={() => onRemove(att.s3Key)}
                disabled={isDownloadDisabled}
                title="Remove attachment"
              >
                <i className="fa-solid fa-trash-can" />
              </button>
            ) : null}
          </div>
        )
      })}

      {pendingFiles.map((file, idx) => {
        const fileName = file.name
        const extension = getFileExtension(fileName, file.type)
        const fileMeta = getFileTypeMeta(extension, file.type)

        return renderCardShell(
          `pending-${fileName}-${idx}`,
          fileMeta,
          fileName,
          <>
            <i className="fa-solid fa-cloud-arrow-up mr-1" />
            Ready to upload on submit
          </>,
          <button
            type="button"
            className="btn btn-sm btn-outline-danger"
            onClick={() => onRemovePending(idx)}
            disabled={isDownloadDisabled}
            style={{ width: '100%' }}
          >
            <i className="fa-solid fa-trash-can mr-1" />
            Remove
          </button>
        )
      })}
    </div>
  )
}

const PublicPageShell = ({ children }) => (
  <div className="wrapper" style={{ minHeight: '100vh' }}>
    <nav
      className="main-header navbar navbar-expand navbar-white navbar-light border-bottom"
      style={{ marginLeft: 0 }}
    >
      <div className="container-fluid">
        <span className="navbar-brand mb-0 h1 d-flex align-items-center">
          <img
            src="/assets/img/Website_logo_color logo-black text-1.png"
            alt="Logo"
            style={{ height: 36 }}
          />
          {/* <span style={{ fontSize: '1.1rem', fontWeight: 600 }}>Request for Quotation</span> */}
        </span>
      </div>
    </nav>
    <div className="content-wrapper" style={{ marginLeft: 0 }}>
      {children}
    </div>
  </div>
)

const RfqSuppliers = () => {
  const {
    id,
    supplierMailId: supplierMailIdParam,
    supplierId: supplierIdParam
  } = useParams()
  const [searchParams] = useSearchParams()
  const supplierMailId = resolveSupplierMailId(supplierMailIdParam, searchParams)
  const supplierId = String(
    supplierIdParam || searchParams.get('supplierId') || ''
  ).trim()
  const [rfq, setRfq] = useState(null)
  const [isLoading, setIsLoading] = useState(true)
  const [loadError, setLoadError] = useState('')
  const [isPriceEditMode, setIsPriceEditMode] = useState(false)
  const [materialPrices, setMaterialPrices] = useState([])
  const [supplierTerms, setSupplierTerms] = useState([])
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [showSubmitSuccess, setShowSubmitSuccess] = useState(false)
  const [submitSuccessMessage, setSubmitSuccessMessage] = useState('')
  const [submitErrors, setSubmitErrors] = useState(emptySubmitErrors)
  const [downloadingS3Key, setDownloadingS3Key] = useState('')
  const [isDownloadingAll, setIsDownloadingAll] = useState(false)
  const [pendingSupplierFiles, setPendingSupplierFiles] = useState([])
  const [removedSupplierAttachmentKeys, setRemovedSupplierAttachmentKeys] = useState(new Set())
  const [isUploadingSupplierAttachments, setIsUploadingSupplierAttachments] = useState(false)
  const [isExportingQuote, setIsExportingQuote] = useState(false)
  const [isImportingQuote, setIsImportingQuote] = useState(false)
  const supplierAttachmentInputRef = useRef(null)
  const quoteImportInputRef = useRef(null)
  const termsSectionRef = useRef(null)

  

  const fetchRfq = useCallback(async () => {
    if (!id) {
      setLoadError('RFQ ID is missing from the link.')
      setIsLoading(false)
      return
    }

    if (!supplierMailId) {
      setLoadError('Supplier email is missing from the link.')
      setIsLoading(false)
      return
    }

    setIsLoading(true)
    setLoadError('')
    try {
      const currentUserId = localStorage.getItem('id') || undefined
      const response = await axios.post(API_URL, {
        action: 'get-rfq-detail-by-id',
        rfqid: id,
        email: supplierMailId,
        ...(currentUserId ? { currentUserId } : {})
      })
      const responseData = normalizeApiPayload(parseApiResponse(response.data))

      if (isApiSuccess(responseData)) {
        const row = pickRfqFromResponse(responseData)
        if (row) {
          setRfq((prev) => {
            const mergedSupplier =
              row.supplier ||
              findSupplierQuote(row, supplierMailId, supplierId) ||
              prev?.supplier ||
              null

            return {
              ...row,
              // Keep price-matching revision metadata if detail API omits it
              previousQuote:
                getSupplierPreviousQuote(row, mergedSupplier) ||
                prev?.previousQuote ||
                null,
              quoteAfterTargetPrice:
                getSupplierQuoteAfterTarget(row, mergedSupplier) ||
                prev?.quoteAfterTargetPrice ||
                null,
              quoteHistory:
                getQuoteHistoryList(row, mergedSupplier).length > 0
                  ? getQuoteHistoryList(row, mergedSupplier)
                  : prev?.quoteHistory || row.quoteHistory,
              supplier: mergedSupplier
                ? {
                    ...(prev?.supplier || {}),
                    ...mergedSupplier,
                    previousQuote:
                      mergedSupplier.previousQuote ||
                      prev?.supplier?.previousQuote ||
                      prev?.previousQuote,
                    quoteAfterTargetPrice:
                      mergedSupplier.quoteAfterTargetPrice ||
                      prev?.supplier?.quoteAfterTargetPrice ||
                      prev?.quoteAfterTargetPrice,
                    quoteHistory:
                      (Array.isArray(mergedSupplier.quoteHistory) &&
                        mergedSupplier.quoteHistory.length > 0
                        ? mergedSupplier.quoteHistory
                        : null) ||
                      prev?.supplier?.quoteHistory ||
                      prev?.quoteHistory
                  }
                : prev?.supplier || row.supplier
            }
          })
        } else {
          setLoadError('RFQ not found.')
        }
      } else {
        const message = responseData?.message || 'Failed to fetch RFQ details'
        setLoadError(message)
        toast.error(message, { autoClose: 2000 })
      }
    } catch (error) {
      console.error('Fetch RFQ detail error:', error)
      const errData = error.response?.data ? parseApiResponse(error.response.data) : null
      const message =
        errData?.message || 'An error occurred while fetching RFQ details'
      setLoadError(message)
      toast.error(message, { autoClose: 2000 })
    } finally {
      setIsLoading(false)
    }
  }, [id, supplierMailId])

  useEffect(() => {
    fetchRfq()
  }, [fetchRfq])

  const materials = useMemo(
    () => (Array.isArray(rfq?.materials) ? rfq.materials : []),
    [rfq]
  )

  const supplierQuote = useMemo(
    () => findSupplierQuote(rfq, supplierMailId, supplierId),
    [rfq, supplierMailId, supplierId]
  )

  const hasExistingQuote = useMemo(
    () => hasQuoteSubmitted(rfq, supplierQuote),
    [rfq, supplierQuote]
  )

  const isQuotationClosed = useMemo(
    () => isRfqDueDatePassed(rfq?.rfqDueDate),
    [rfq?.rfqDueDate]
  )

  const canUpdateQuote = !isQuotationClosed

  const showMaterialPricing = hasExistingQuote || (isPriceEditMode && canUpdateQuote)
  const showTermsComments = hasExistingQuote || (isPriceEditMode && canUpdateQuote)

  const previousQuoteRecord = useMemo(
    () => getSupplierPreviousQuote(rfq, supplierQuote),
    [rfq, supplierQuote]
  )
  const quoteAfterTargetRecord = useMemo(
    () => getSupplierQuoteAfterTarget(rfq, supplierQuote),
    [rfq, supplierQuote]
  )
  const previousQuoteMaxTotal = useMemo(
    () => getPreviousQuoteMaxTotal(rfq, supplierQuote),
    [rfq, supplierQuote]
  )
  // Price-matching cap only when targetPrice > 0 AND this supplier already quoted
  const isPriceMatchingMode =
    hasRfqTargetPrice(rfq) &&
    (hasExistingQuote ||
      Boolean(previousQuoteRecord) ||
      (Number.isFinite(previousQuoteMaxTotal) && previousQuoteMaxTotal > 0))

  /** RFQ-level total target only (line/BOQ targets are separate). */
  const rfqLevelPriceMatchTarget = useMemo(
    () => getRfqEffectiveTargetPrice(rfq),
    [rfq]
  )
  const hasRfqLevelPriceMatchTarget =
    Number.isFinite(rfqLevelPriceMatchTarget) && rfqLevelPriceMatchTarget > 0

  const termsRows = useMemo(() => (rfq ? getTermsRows(rfq) : []), [rfq])

  const rfqOwnerAttachments = useMemo(() => {
    if (!Array.isArray(rfq?.rfqOwner)) return []
    return rfq.rfqOwner.map(mapRfqOwnerFromApi).filter((att) => att.s3Key)
  }, [rfq])

  const savedSupplierAttachments = useMemo(
    () => getSupplierAttachmentsFromRfq(rfq, supplierQuote),
    [rfq, supplierQuote]
  )

  const visibleSupplierAttachments = useMemo(
    () =>
      savedSupplierAttachments.filter((att) => !removedSupplierAttachmentKeys.has(att.s3Key)),
    [savedSupplierAttachments, removedSupplierAttachmentKeys]
  )

  const supplierAttachmentCount =
    visibleSupplierAttachments.length + pendingSupplierFiles.length

  const downloadRfqOwnerAttachment = async (s3Key) => {
    if (!s3Key || downloadingS3Key || isDownloadingAll) return
    setDownloadingS3Key(s3Key)
    try {
      const response = await axios.post(API_URL, {
        action: 'get-rfq-owner-attachment-download-presigned-url',
        s3Keys: [s3Key],
        expiresIn: 3600
      })
      const responseData = normalizeApiPayload(parseApiResponse(response.data))
      if (!isApiSuccess(responseData)) {
        toast.error(responseData?.message || 'Failed to get download URL', { autoClose: 2000 })
        return
      }
      const downloadUrl =
        responseData.files?.[0]?.downloadUrl || responseData.downloadUrl
      if (!downloadUrl) {
        toast.error('Download URL not available', { autoClose: 2000 })
        return
      }
      window.open(downloadUrl, '_blank', 'noopener,noreferrer')
    } catch (error) {
      console.error('Download attachment error:', error)
      toast.error('Failed to download attachment', { autoClose: 2000 })
    } finally {
      setDownloadingS3Key('')
    }
  }

  const downloadAllRfqOwnerAttachments = async () => {
    if (rfqOwnerAttachments.length === 0 || downloadingS3Key || isDownloadingAll) return

    setIsDownloadingAll(true)
    try {
      const s3Keys = rfqOwnerAttachments.map((att) => att.s3Key).filter(Boolean)
      const response = await axios.post(API_URL, {
        action: 'get-rfq-owner-attachment-download-presigned-url',
        s3Keys,
        expiresIn: 3600
      })
      const responseData = normalizeApiPayload(parseApiResponse(response.data))
      if (!isApiSuccess(responseData)) {
        toast.error(responseData?.message || 'Failed to get download URLs', { autoClose: 2000 })
        return
      }

      const files = Array.isArray(responseData.files) ? responseData.files : []
      const urls = files
        .map((file) => file?.downloadUrl)
        .filter((url) => typeof url === 'string' && url.length > 0)

      if (urls.length === 0 && responseData.downloadUrl) {
        urls.push(responseData.downloadUrl)
      }

      if (urls.length === 0) {
        toast.error('Download URLs not available', { autoClose: 2000 })
        return
      }

      urls.forEach((url, index) => {
        setTimeout(() => {
          window.open(url, '_blank', 'noopener,noreferrer')
        }, index * 350)
      })

      if (urls.length > 1) {
        toast.success(`Opening ${urls.length} downloads`, { autoClose: 2000 })
      }
    } catch (error) {
      console.error('Download all attachments error:', error)
      toast.error('Failed to download attachments', { autoClose: 2000 })
    } finally {
      setIsDownloadingAll(false)
    }
  }

  const downloadSupplierAttachment = async (s3Key) => {
    if (!s3Key || downloadingS3Key || isDownloadingAll) return
    setDownloadingS3Key(s3Key)
    try {
      // Download via GetObject presign — not the upload URL (x-id=PutObject).
      // Client portal: rfqid + email only.
      const response = await axios.post(API_URL, {
        action: 'get-rfq-supplier-attachment-download-presigned-url',
        rfqid: id,
        email: supplierMailId,
        s3Keys: [s3Key],
        expiresIn: 3600,
      })
      const responseData = normalizeApiPayload(parseApiResponse(response.data))
      if (!isApiSuccess(responseData)) {
        toast.error(responseData?.message || 'Failed to get download URL', { autoClose: 2000 })
        return
      }
      const downloadUrl =
        responseData.files?.[0]?.downloadUrl || responseData.downloadUrl
      if (!downloadUrl) {
        toast.error('Download URL not available', { autoClose: 2000 })
        return
      }
      window.open(downloadUrl, '_blank', 'noopener,noreferrer')
    } catch (error) {
      console.error('Download supplier attachment error:', error)
      toast.error('Failed to download attachment', { autoClose: 2000 })
    } finally {
      setDownloadingS3Key('')
    }
  }

  const handleSupplierAttachmentFileSelect = (e) => {
    const files = Array.from(e.target.files || [])
    e.target.value = ''
    if (files.length === 0) return

    const invalidFiles = files.filter((file) => !isSupplierFileAllowed(file))
    if (invalidFiles.length > 0) {
      toast.error(
        `Invalid file type. Allowed formats: ${formatAllowedFormatsList()}`,
        { autoClose: 3000 }
      )
      return
    }

    const totalCount = supplierAttachmentCount + files.length
    if (totalCount > MAX_SUPPLIER_ATTACHMENTS) {
      toast.error(`Maximum ${MAX_SUPPLIER_ATTACHMENTS} attachments allowed`, {
        autoClose: 2000
      })
      return
    }

    setPendingSupplierFiles((prev) => [...prev, ...files])
  }

  const removePendingSupplierFile = (index) => {
    setPendingSupplierFiles((prev) => prev.filter((_, i) => i !== index))
  }

  const markSupplierAttachmentForRemoval = (s3Key) => {
    setRemovedSupplierAttachmentKeys((prev) => {
      const next = new Set(prev)
      next.add(s3Key)
      return next
    })
  }

  const uploadPendingSupplierAttachments = async () => {
    if (pendingSupplierFiles.length === 0) return []

    setIsUploadingSupplierAttachments(true)
    try {
      const files = pendingSupplierFiles
      // Prefer browser MIME; fall back by extension for docx/xlsx (often empty type).
      const contentTypesForPresign = files.map((f) => resolveFileContentType(f))

      // 1) Get presigned URLs — API axios is OK here (not the S3 PUT).
      // Client portal: rfqid + email only (no currentUserId required by backend).
      const response = await axios.post(API_URL, {
        action: 'get-rfq-supplier-attachment-upload-presigned-url',
        rfqid: id,
        email: supplierMailId,
        fileNames: files.map((f) => f.name),
        contentTypes: contentTypesForPresign,
        expiresIn: 7200,
      })
      const responseData = normalizeApiPayload(parseApiResponse(response.data))

      if (!isApiSuccess(responseData) || !Array.isArray(responseData.files)) {
        const invalidMsg = Array.isArray(responseData?.invalidFiles)
          ? `${responseData.message || 'Invalid file type'}`
          : responseData?.message || 'Failed to get upload URLs'
        toast.error(invalidMsg, { autoClose: 3000 })
        throw new Error(invalidMsg)
      }

      const presignedFiles = responseData.files
      if (presignedFiles.length !== files.length) {
        throw new Error('Upload URL count mismatch')
      }

      // 2) Upload each file to S3 via raw fetch — NEVER shared API axios
      // (interceptor would replace File with {"currentUserId":"..."} ~44B).
      for (let i = 0; i < files.length; i++) {
        const meta = presignedFiles[i]
        const file = files[i]
        const contentType =
          meta.contentType ||
          contentTypesForPresign[i] ||
          'application/octet-stream'

        if (!meta.uploadUrl) {
          throw new Error(`Missing uploadUrl for ${file.name}`)
        }
        if (!(file instanceof Blob) || file.size <= 0) {
          throw new Error(`Invalid file for upload: ${file.name}`)
        }

        const putRes = await fetch(meta.uploadUrl, {
          method: 'PUT',
          headers: {
            // Must match the Content-Type the URL was signed with
            'Content-Type': contentType,
          },
          body: file, // raw File/Blob — NOT FormData, NOT JSON
        })

        if (!putRes.ok) {
          throw new Error(
            `S3 upload failed for ${file.name}: ${putRes.status}`
          )
        }
      }

      // 3) Return keys only after all PUTs succeeded (caller saves quote next)
      return presignedFiles.map((meta, i) => ({
        s3Key: meta.s3Key,
        originalFileName:
          meta.originalFileName || meta.fileName || files[i].name,
        contentType:
          meta.contentType ||
          contentTypesForPresign[i] ||
          'application/octet-stream',
      }))
    } finally {
      setIsUploadingSupplierAttachments(false)
    }
  }

  useEffect(() => {
    if (isQuotationClosed && isPriceEditMode) {
      setIsPriceEditMode(false)
    }
  }, [isQuotationClosed, isPriceEditMode])

  useEffect(() => {
    // Don't wipe in-progress price edits when RFQ metadata refreshes
    if (isPriceEditMode) return
    setMaterialPrices(buildInitialMaterialPrices(materials, rfq, supplierQuote))
  }, [materials, rfq, supplierQuote, isPriceEditMode])

  useEffect(() => {
    if (isPriceEditMode) return
    setSupplierTerms(buildInitialSupplierTerms(rfq, supplierQuote))
  }, [rfq, supplierQuote, isPriceEditMode])

  const pricedMaterials = useMemo(() => {
    return materials.map((material, index) => {
      const priceRow = materialPrices[index] || {
        basicPrice: '',
        gstPercent: '',
        hsn: '',
        comments: '',
        boqPrices: []
      }
      return {
        material,
        priceRow,
        ...calculateMaterialPricing(material, priceRow)
      }
    })
  }, [materials, materialPrices])

  /** Hide Target Price column when every line is already at/below its target. */
  const showPriceMatchTargetColumn = useMemo(() => {
    if (!isPriceMatchingMode) return false
    return materials.some((material, index) => {
      const priceRow = materialPrices[index]
      const existing = findMaterialQuote(
        material,
        index,
        supplierQuote?.materialQuotes
      )
      const boqItems = getMaterialBoqItems(material)
      if (boqItems.length > 0) {
        return boqItems.some((boqItem, boqIndex) => {
          const target = getBoqEffectiveTargetPrice(boqItem)
          const saved = parseDecimalValue(
            findBoqQuotePrice(boqItem, boqIndex, existing) ||
              boqItem.basicPrice
          )
          const edited = parseDecimalValue(
            priceRow?.boqPrices?.[boqIndex]?.basicPrice
          )
          const quoted = Number.isFinite(edited)
            ? edited
            : Number.isFinite(saved)
              ? saved
              : NaN
          return shouldShowPriceMatchTarget(quoted, target)
        })
      }
      const target = getMaterialEffectiveTargetPrice(material)
      const saved = parseDecimalValue(
        existing?.basicPrice ?? material.basicPrice
      )
      const edited = parseDecimalValue(priceRow?.basicPrice)
      const quoted = Number.isFinite(edited)
        ? edited
        : Number.isFinite(saved)
          ? saved
          : NaN
      return shouldShowPriceMatchTarget(quoted, target)
    })
  }, [
    isPriceMatchingMode,
    materials,
    materialPrices,
    supplierQuote
  ])

  const grossTotals = useMemo(() => {
    const calculated = pricedMaterials.reduce(
      (acc, row) => ({
        grossBasicPrice: acc.grossBasicPrice + row.lineBasic,
        grossGstValue: acc.grossGstValue + row.gstValue,
        grossTotalValue: acc.grossTotalValue + row.totalValue
      }),
      { grossBasicPrice: 0, grossGstValue: 0, grossTotalValue: 0 }
    )

    if (!hasExistingQuote || isPriceEditMode) {
      return calculated
    }

    return getGrossTotalsFromRfq(rfq, supplierQuote, calculated)
  }, [pricedMaterials, hasExistingQuote, isPriceEditMode, rfq, supplierQuote])

  const exceedsPreviousQuoteMax =
    isPriceMatchingMode &&
    isPriceEditMode &&
    Number.isFinite(previousQuoteMaxTotal) &&
    previousQuoteMaxTotal > 0 &&
    roundMoney(grossTotals.grossTotalValue) > roundMoney(previousQuoteMaxTotal)


  const clearMaterialFieldError = (index, field, boqIndex = null) => {
    setSubmitErrors((prev) => {
      const rowErrors = prev.materials[index]
      if (!rowErrors) return prev

      const nextRowErrors = { ...rowErrors }

      if (field === 'boqPrices' && boqIndex != null) {
        if (!rowErrors.boqPrices?.[boqIndex]) return prev
        const nextBoqErrors = { ...rowErrors.boqPrices }
        delete nextBoqErrors[boqIndex]
        if (Object.keys(nextBoqErrors).length === 0) {
          delete nextRowErrors.boqPrices
        } else {
          nextRowErrors.boqPrices = nextBoqErrors
        }
      } else {
        if (!rowErrors[field]) return prev
        delete nextRowErrors[field]
      }

      const nextMaterials = { ...prev.materials }
      if (Object.keys(nextRowErrors).length === 0) {
        delete nextMaterials[index]
      } else {
        nextMaterials[index] = nextRowErrors
      }

      return { ...prev, materials: nextMaterials, formMessage: '' }
    })
  }

  const handleMaterialPriceChange = (index, field, value) => {
    let nextValue = value
    if (field !== 'comments' && field !== 'hsn') {
      if (!isDecimalInput(value)) return
      // Normalize pasted values like 23,580 / 23,580.00 → 23580 / 23580.00
      nextValue = normalizeDecimalInput(value)
      if (!isDecimalInput(nextValue)) return
    }
    clearMaterialFieldError(index, field)
    setMaterialPrices((prev) => {
      const next = [...prev]
      next[index] = { ...next[index], [field]: nextValue }
      return next
    })
  }

  const handleBoqBasicPriceChange = (materialIndex, boqIndex, value) => {
    if (!isDecimalInput(value)) return
    const nextValue = normalizeDecimalInput(value)
    if (!isDecimalInput(nextValue)) return
    clearMaterialFieldError(materialIndex, 'boqPrices', boqIndex)
    setMaterialPrices((prev) => {
      const next = [...prev]
      const row = { ...(next[materialIndex] || {}) }
      const boqPrices = [...(row.boqPrices || [])]
      while (boqPrices.length <= boqIndex) {
        boqPrices.push({ basicPrice: '' })
      }
      boqPrices[boqIndex] = {
        ...boqPrices[boqIndex],
        basicPrice: nextValue
      }
      row.boqPrices = boqPrices
      next[materialIndex] = row
      return next
    })
  }

  const handleTermsCommentChange = (index, value) => {
    setSubmitErrors((prev) => {
      if (!prev.terms[index]) return prev
      const nextTerms = { ...prev.terms }
      delete nextTerms[index]
      return { ...prev, terms: nextTerms, formMessage: '' }
    })
    setSupplierTerms((prev) => {
      const next = [...prev]
      next[index] = { ...next[index], comments: value }
      return next
    })
  }

  const handleStartPriceUpdate = () => {
    if (isRfqDueDatePassed(rfq?.rfqDueDate)) {
      setSubmitErrors({
        ...emptySubmitErrors(),
        formMessage: 'RFQ due date has passed. Quotation can no longer be updated.'
      })
      return
    }
    setSubmitErrors(emptySubmitErrors())
    setPendingSupplierFiles([])
    setRemovedSupplierAttachmentKeys(new Set())
    setMaterialPrices(buildInitialMaterialPrices(materials, rfq, supplierQuote))
    setSupplierTerms(buildInitialSupplierTerms(rfq, supplierQuote))
    setIsPriceEditMode(true)
    requestAnimationFrame(() => {
      termsSectionRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    })
  }

  const buildSupplierQuoteIdentityPayload = () => {
    const payload = {
      action: '',
      rfqid: id
    }
    if (supplierMailId) {
      payload.supplierMailId = supplierMailId
      payload.email = supplierMailId
    }
    if (supplierId) {
      payload.supplierId = supplierId
    }
    const currentUserId = localStorage.getItem('id')
    if (currentUserId) {
      payload.currentUserId = currentUserId
    }
    return payload
  }

  const handleExportSupplierQuote = async () => {
    if (!id) {
      toast.error('RFQ ID is missing', { autoClose: 2000 })
      return
    }
    if (!supplierMailId && !supplierId) {
      toast.error('Supplier identity is missing', { autoClose: 2000 })
      return
    }
    if (isExportingQuote || isImportingQuote) return

    setIsExportingQuote(true)
    try {
      const payload = {
        ...buildSupplierQuoteIdentityPayload(),
        action: 'export-rfq-supplier-quote'
      }
      const response = await axios.post(API_URL, payload, {
        responseType: 'json'
      })
      const responseData = normalizeApiPayload(parseApiResponse(response.data))
      const base64 =
        extractExcelBase64(responseData) || extractExcelBase64(response.data)

      if (!base64) {
        throw new Error(responseData?.message || 'Invalid export response')
      }

      const rfqNoSafe = String(rfq?.rfqNumber || rfq?.rfqNo || id).replace(
        /[^\w.-]+/g,
        '_'
      )
      downloadXlsxFromBase64(base64, `RFQ-Quote-${rfqNoSafe}.xlsx`)
      toast.success(
        responseData?.message ||
          'Quote Excel exported. Yellow cells are editable — do not change LineKey.',
        { autoClose: 3000 }
      )
    } catch (error) {
      console.error('Export RFQ supplier quote error:', error)
      const errData = error.response?.data
        ? parseApiResponse(error.response.data)
        : null
      toast.error(
        errData?.message || error.message || 'Failed to export quote Excel',
        { autoClose: 3000 }
      )
    } finally {
      setIsExportingQuote(false)
    }
  }

  /**
   * Apply Excel rows to browser form state only (no DB write).
   * Values are saved when the user clicks Submit.
   */
  const applyExcelRowsToForm = (materialRows, termRows) => {
    const rows = Array.isArray(materialRows) ? materialRows : []
    const terms = Array.isArray(termRows) ? termRows : []
    if (rows.length === 0 && terms.length === 0) {
      return { applied: 0, termsApplied: 0 }
    }

    const lineMap = buildExcelLineTargetMap(materials)
    let applied = 0

    const nextPrices = (Array.isArray(materialPrices) ? materialPrices : []).map(
      (row) => ({
        ...row,
        boqPrices: Array.isArray(row?.boqPrices)
          ? row.boqPrices.map((bp) => ({ ...bp }))
          : []
      })
    )

    rows.forEach((row, rowIndex) => {
      const hasEditableValue =
        pickExcelField(row, EXCEL_FIELD_ALIASES.basicPrice) ||
        pickExcelField(row, EXCEL_FIELD_ALIASES.hsn) ||
        pickExcelField(row, EXCEL_FIELD_ALIASES.gstPercent) ||
        pickExcelField(row, EXCEL_FIELD_ALIASES.technicalResponse) ||
        pickExcelField(row, EXCEL_FIELD_ALIASES.lineKey) ||
        pickExcelField(row, EXCEL_FIELD_ALIASES.materialCode)
      if (!hasEditableValue) return

      const target = resolveExcelRowTarget(row, lineMap, materials, rowIndex)
      if (!target || !nextPrices[target.index]) return

      const basicPrice = pickExcelField(row, EXCEL_FIELD_ALIASES.basicPrice)
      const hsn = pickExcelField(row, EXCEL_FIELD_ALIASES.hsn)
      const gstPercent = pickExcelField(row, EXCEL_FIELD_ALIASES.gstPercent)
      const technicalResponse = pickExcelField(
        row,
        EXCEL_FIELD_ALIASES.technicalResponse
      )

      if (target.type === 'boq') {
        // BOQ Excel rows only carry Basic Price; HSN/GST/Technical stay on parent material.
        // Do not copy "-" placeholders from child rows onto the parent.
        const boqPrices = Array.isArray(nextPrices[target.index].boqPrices)
          ? [...nextPrices[target.index].boqPrices]
          : []
        while (boqPrices.length <= target.boqIndex) {
          boqPrices.push({ basicPrice: '' })
        }
        if (basicPrice !== '') {
          boqPrices[target.boqIndex] = {
            ...boqPrices[target.boqIndex],
            basicPrice
          }
          nextPrices[target.index] = {
            ...nextPrices[target.index],
            boqPrices
          }
          applied += 1
        }
        return
      }

      const patch = { ...nextPrices[target.index] }
      let touched = false
      if (basicPrice !== '') {
        patch.basicPrice = basicPrice
        touched = true
      }
      if (hsn !== '') {
        patch.hsn = hsn
        touched = true
      }
      if (gstPercent !== '') {
        patch.gstPercent = gstPercent
        touched = true
      }
      if (technicalResponse !== '') {
        patch.comments = toEditableComment(technicalResponse)
        touched = true
      }
      if (touched) {
        nextPrices[target.index] = patch
        applied += 1
      }
    })

    if (applied > 0) {
      setMaterialPrices(nextPrices)
    }

    let termsApplied = 0
    if (terms.length > 0) {
      const nextTerms = (Array.isArray(supplierTerms) ? supplierTerms : []).map(
        (row) => ({ ...row })
      )
      terms.forEach((row, index) => {
        const rfqTerm = pickExcelField(row, EXCEL_FIELD_ALIASES.rfqTerm)
        const quoteResponse = pickExcelField(
          row,
          EXCEL_FIELD_ALIASES.quoteResponse
        )
        if (quoteResponse === '' && !rfqTerm) return

        let targetIndex = -1
        if (rfqTerm) {
          targetIndex = nextTerms.findIndex(
            (t) =>
              String(t.rfqTerm || '')
                .trim()
                .toLowerCase() === rfqTerm.toLowerCase()
          )
        }
        if (targetIndex < 0 && nextTerms[index]) targetIndex = index
        if (targetIndex < 0 || !nextTerms[targetIndex]) return

        if (quoteResponse !== '') {
          nextTerms[targetIndex] = {
            ...nextTerms[targetIndex],
            comments: toEditableComment(quoteResponse)
          }
          termsApplied += 1
        }
      })
      if (termsApplied > 0) {
        setSupplierTerms(nextTerms)
      }
    }

    return { applied, termsApplied }
  }

  const handleImportSupplierQuoteFile = async (event) => {
    const file = event.target.files?.[0]
    event.target.value = ''

    if (!file) return
    if (isQuotationClosed) {
      toast.error(
        'RFQ due date has passed. Quotation can no longer be updated.',
        { autoClose: 2500 }
      )
      return
    }

    const lowerName = String(file.name || '').toLowerCase()
    if (!lowerName.endsWith('.xlsx') && !lowerName.endsWith('.xls')) {
      toast.error('Please upload an Excel file (.xlsx)', { autoClose: 2500 })
      return
    }
    if (isExportingQuote || isImportingQuote) return

    setIsImportingQuote(true)
    setSubmitErrors(emptySubmitErrors())
    try {
      // Client-side only: fill table in browser. DB updates on Submit.
      const { materials: materialRows, terms: termRows } =
        await parseSupplierQuoteExcelFile(file)
      const { applied, termsApplied } = applyExcelRowsToForm(
        materialRows,
        termRows
      )

      if (applied === 0 && termsApplied === 0) {
        toast.error(
          'No matching quote rows found in Excel. Keep LineKey unchanged from export.',
          { autoClose: 3500 }
        )
        return
      }

      setIsPriceEditMode(true)
      setShowSubmitSuccess(false)
      toast.success(
        `Imported into the form (${applied} material line${
          applied === 1 ? '' : 's'
        }${
          termsApplied > 0
            ? `, ${termsApplied} term${termsApplied === 1 ? '' : 's'}`
            : ''
        }). Review values, then click Submit to save.`,
        { autoClose: 4000 }
      )
    } catch (error) {
      console.error('Import RFQ supplier quote error:', error)
      toast.error(
        error.message || 'Failed to read quote Excel in the browser',
        { autoClose: 3000 }
      )
    } finally {
      setIsImportingQuote(false)
    }
  }

  const handleCancelPriceUpdate = () => {
    setMaterialPrices(buildInitialMaterialPrices(materials, rfq, supplierQuote))
    setSupplierTerms(buildInitialSupplierTerms(rfq, supplierQuote))
    setSubmitErrors(emptySubmitErrors())
    setPendingSupplierFiles([])
    setRemovedSupplierAttachmentKeys(new Set())
    setIsPriceEditMode(false)
  }

  const scrollToFirstInvalidField = () => {
  requestAnimationFrame(() => {
    const firstInvalid = document.querySelector('.is-invalid')
    if (firstInvalid) {
      firstInvalid.scrollIntoView({ behavior: 'smooth', block: 'center' })
      if (typeof firstInvalid.focus === 'function') {
        firstInvalid.focus({ preventScroll: true })
      }
    }
  })
}

  const handleSubmitPrices = async () => {
    if (isRfqDueDatePassed(rfq?.rfqDueDate)) {
      setSubmitErrors({
        ...emptySubmitErrors(),
        formMessage: 'RFQ due date has passed. Quotation can no longer be submitted.'
      })
      return
    }

    if (!supplierMailId) {
      setSubmitErrors({
        ...emptySubmitErrors(),
        formMessage:
          'Supplier email is missing from the link. Please use the link from your RFQ email.'
      })
      return
    }

    const { hasErrors, errors } = validateQuoteSubmission(
      materialPrices,
      supplierTerms,
      materials
    )
    if (hasErrors) {
      setSubmitErrors(errors)
      scrollToFirstInvalidField()
      return
    }

    if (
      isPriceMatchingMode &&
      Number.isFinite(previousQuoteMaxTotal) &&
      previousQuoteMaxTotal > 0 &&
      roundMoney(grossTotals.grossTotalValue) >
        roundMoney(previousQuoteMaxTotal)
    ) {
      const currencyLabel = rfq?.rfqCurrency ? ` ${rfq.rfqCurrency}` : ''
      setSubmitErrors({
        ...emptySubmitErrors(),
        formMessage: `Total Value cannot exceed your previous quotation of ${formatAmount(
          previousQuoteMaxTotal
        )}${currencyLabel}. Enter the same amount or lower.`
      })
      return
    }

    setSubmitErrors(emptySubmitErrors())
    setIsSubmitting(true)
    try {
      let newlyUploadedAttachments = []
      if (pendingSupplierFiles.length > 0) {
        newlyUploadedAttachments = await uploadPendingSupplierAttachments()
      }

      const finalAttachments = [
        ...visibleSupplierAttachments,
        ...newlyUploadedAttachments
      ]

      const currentUserId = localStorage.getItem('id') || undefined

      const payload = {
        action: 'update-rfq-supplier-quote',
        rfqid: id,
        supplierMailId,
        currentUserId,
        terms: supplierTerms.map((row) => ({
          rfqTerm: String(row.rfqTerm || '').trim(),
          termsResponse: String(row.termsResponse || '').trim(),
          comments: String(row.comments || '').trim() || '-'
        })),
        materialQuotes: pricedMaterials.map((row, index) => {
          const boqItems = getMaterialBoqItems(row.material)
          const hasBoq = boqItems.length > 0
          const gstPercent = parseDecimalValue(row.priceRow.gstPercent) || 0
          const lineNo =
            row.material.lineNo != null
              ? row.material.lineNo
              : row.material.sno != null
                ? row.material.sno
                : index + 1

          // BOQ parent basicPrice = Σ(child basicPrice × child qty)
          // Non-BOQ keeps line total (unit × qty) for backward compatibility
          const quoteRow = {
            materialCode: row.material.materialCode || '',
            materialDescription: row.material.materialDescription || '',
            lineNo,
            hsn: String(row.priceRow.hsn || '').trim(),
            basicPrice: hasBoq ? row.basicPrice : row.lineBasic,
            gstPercent,
            gstValue: row.gstValue,
            totalValue: row.totalValue,
            comments: (row.priceRow.comments || '').trim() || '-'
          }
          const prId = getMaterialPrId(row.material)
          if (prId) {
            quoteRow.pr_id = prId
          }
          if (hasBoq) {
            quoteRow.isBoq = true
            quoteRow.boq = boqItems.map((boqItem, boqIndex) => {
              const boqPayload = {
                boqNo: boqItem.boqNo || '',
                materialCode: boqItem.materialCode || '',
                materialDescription: boqItem.materialDescription || '',
                uom: boqItem.uom || '',
                quantity: parseFloat(boqItem.quantity) || 0,
                basicPrice:
                  parseDecimalValue(
                    row.priceRow.boqPrices?.[boqIndex]?.basicPrice
                  ) || 0
              }
              const boqId = getBoqItemId(boqItem)
              if (boqId) {
                boqPayload.boq_id = boqId
              }
              const hsnCode = String(
                boqItem.hsn || boqItem.hsnCode || ''
              ).trim()
              if (hsnCode) {
                boqPayload.hsnCode = hsnCode
              }
              return boqPayload
            })
          }
          return quoteRow
        }),
        grossTotalBasicPrice: grossTotals.grossBasicPrice,
        grossGstValue: grossTotals.grossGstValue,
        grossTotalValue: grossTotals.grossTotalValue
      }

      if (supplierId) {
        payload.supplierId = supplierId
      }

      if (finalAttachments.length > 0) {
        payload.attachments = finalAttachments.map((att) => ({
          s3Key: att.s3Key,
          originalFileName: att.originalFileName,
          contentType: att.contentType
        }))
      } else if (
        savedSupplierAttachments.length > 0 ||
        removedSupplierAttachmentKeys.size > 0
      ) {
        payload.attachments = []
      }

      const response = await axios.post(API_URL, payload)
      const responseData = normalizeApiPayload(parseApiResponse(response.data))

      if (isApiSuccess(responseData)) {
        const updatedSupplier = extractSupplierFromQuoteUpdateResponse(
          responseData,
          supplierMailId,
          supplierId
        )

        if (updatedSupplier) {
          setRfq((prev) => {
            if (!prev) return prev
            const nextSuppliers = Array.isArray(responseData?.data?.suppliers)
              ? responseData.data.suppliers
              : Array.isArray(prev.suppliers)
                ? prev.suppliers.map((s) => {
                    const sameId =
                      getSupplierRecordId(s) &&
                      getSupplierRecordId(s) === getSupplierRecordId(updatedSupplier)
                    const sameMail =
                      String(s?.supplierMailId || '').trim().toLowerCase() ===
                      String(updatedSupplier.supplierMailId || '')
                        .trim()
                        .toLowerCase()
                    return sameId || sameMail ? { ...s, ...updatedSupplier } : s
                  })
                : prev.suppliers

            return {
              ...prev,
              supplier: updatedSupplier,
              suppliers: nextSuppliers,
              quoteSubmitted: true,
              quoteSubmittedAt:
                updatedSupplier.quoteSubmittedAt || prev.quoteSubmittedAt,
              previousQuote:
                updatedSupplier.previousQuote || prev.previousQuote,
              quoteAfterTargetPrice:
                updatedSupplier.quoteAfterTargetPrice || prev.quoteAfterTargetPrice,
              quoteHistory:
                updatedSupplier.quoteHistory || prev.quoteHistory,
              grossTotal: {
                grossTotalBasicPrice:
                  updatedSupplier.grossTotalBasicPrice ??
                  prev.grossTotal?.grossTotalBasicPrice,
                grossGstValue:
                  updatedSupplier.grossGstValue ?? prev.grossTotal?.grossGstValue,
                grossTotalValue:
                  updatedSupplier.grossTotalValue ?? prev.grossTotal?.grossTotalValue
              }
            }
          })
        }

        const thankYouMessage =
          responseData?.message ||
          (updatedSupplier?.previousQuote
            ? 'Your updated quotation has been submitted successfully.'
            : 'Thank you for your response. Your quotation has been submitted successfully.')
        setSubmitSuccessMessage(thankYouMessage)
        setShowSubmitSuccess(true)
        setIsPriceEditMode(false)
        setPendingSupplierFiles([])
        setRemovedSupplierAttachmentKeys(new Set())
        fetchRfq()
      } else {
        toast.error(responseData?.message || 'Failed to submit quote', { autoClose: 2000 })
      }
    } catch (error) {
      console.error('Submit RFQ quote error:', error)
      const errData = error.response?.data ? parseApiResponse(error.response.data) : null
      toast.error(
        errData?.message ||
          error.message ||
          'An error occurred while submitting quote',
        { autoClose: 3000 }
      )
    } finally {
      setIsSubmitting(false)
    }
  }

  const header = (
    <div className="content-header">
      <div className="container-fluid">
        <div className="row mb-2 align-items-center">
          <div className="col-12">
            <h1 className="m-0 txt_lft">Request for Quotation</h1>
          </div>
        </div>
      </div>
    </div>
  )

  if (isLoading) {
    return (
      <PublicPageShell>
        {header}
        <div className="content">
          <div className="container-fluid">
            <div className="row">
              <div className="frmwdth_cntr_rfq">
                <div className="col-lg-12">
                  <div className="card card-primary card-outline">
                    <div className="card-body text-center py-5">
                      <PulseLoader color="#1199B2" size={10} />
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </PublicPageShell>
    )
  }

  if (!rfq) {
    return (
      <PublicPageShell>
        {header}
        <div className="content">
          <div className="container-fluid">
            <div className="row">
              <div className="frmwdth_cntr_rfq">
                <div className="col-lg-12">
                  <div className="card card-primary card-outline">
                    <div className="card-body text-center py-5">
                      <p className="text-muted mb-0">{loadError || 'RFQ not found'}</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </PublicPageShell>
    )
  }

  const rfqNo = rfq.rfqNumber || rfq.rfqNo || '-'
  const currency = rfq.rfqCurrency || ''

  return (
    <PublicPageShell>
      {header}
      <div className="content">
        <div className="container-fluid">
          <div className="row">
            <div className="frmwdth_cntr_rfq">
              <div className="col-lg-12">
                <div className="card card-primary card-outline">
                  <div className="card-body">
                    <SectionTitle icon="fa-file-lines" title="RFQ Details" />

                    <ViewField label="RFQ Number">{rfqNo}</ViewField>
                    <ViewField label="RFQ Date">{formatDate(rfq.rfqDate)}</ViewField>
                    <ViewField label="RFQ Due Date">{formatDueDateTime(rfq.rfqDueDate)}</ViewField>
                    <ViewField label="Currency">{rfq.rfqCurrency || '-'}</ViewField>
                    <ViewField label="RFQ Summary">
                      <span style={{ whiteSpace: 'pre-wrap' }}>{rfq.rfqSummary || '-'}</span>
                    </ViewField>
                    {isPriceMatchingMode && showPriceMatchTargetColumn ? (
                      <ViewField label="Price Matching">
                        {hasRfqLevelPriceMatchTarget ? (
                          <span className="fwt_600 text-primary">
                            {formatAmount(rfqLevelPriceMatchTarget)}
                            {currency ? ` ${currency}` : ''}
                          </span>
                        ) : (
                          <span className="fwt_600 text-primary">
                            Line targets apply
                          </span>
                        )}
                        <div
                          className="text-muted mt-1"
                          style={{ fontSize: '12px' }}
                        >
                          {hasRfqLevelPriceMatchTarget
                            ? 'Line targets above your quote are shown in the Materials table'
                            : 'No RFQ total target. Per-line / BOQ targets above your quote are shown under Target Price'}
                          {hasRfqLevelPriceMatchTarget &&
                          Number(rfq?.allSuppliersTargetPrice?.targetPrice) >
                            0 &&
                          Number(rfqLevelPriceMatchTarget) > 0 &&
                          Number(rfq.allSuppliersTargetPrice.targetPrice) !==
                            Number(rfqLevelPriceMatchTarget)
                            ? ` · All-suppliers total: ${formatAmount(
                                rfq.allSuppliersTargetPrice.targetPrice
                              )}${currency ? ` ${currency}` : ''}`
                            : ''}
                          .
                        </div>
                      </ViewField>
                    ) : null}

                    {rfqOwnerAttachments.length > 0 ? (
                      <>
                        <div className="row mb-2 mt-3 align-items-center">
                          <div className="col-12 col-md">
                            <h5 className="text-primary border-bottom pb-2 mb-0">
                              <i className="fa-solid fa-paperclip mr-2" />
                              RFQ Attachments
                              <span
                                className="badge badge-info ml-2"
                                style={{ fontSize: '11px', verticalAlign: 'middle' }}
                              >
                                {rfqOwnerAttachments.length}{' '}
                                {rfqOwnerAttachments.length === 1 ? 'file' : 'files'}
                              </span>
                            </h5>
                          </div>
                          {rfqOwnerAttachments.length > 1 ? (
                            <div className="col-12 col-md-auto mt-2 mt-md-0 text-md-right">
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-primary"
                                onClick={downloadAllRfqOwnerAttachments}
                                disabled={Boolean(downloadingS3Key) || isDownloadingAll}
                              >
                                {isDownloadingAll ? (
                                  <>
                                    <span
                                      className="spinner-border spinner-border-sm mr-1"
                                      role="status"
                                      aria-hidden="true"
                                    />
                                    Preparing...
                                  </>
                                ) : (
                                  <>
                                    <i className="fa-solid fa-download mr-1" />
                                    Download All
                                  </>
                                )}
                              </button>
                            </div>
                          ) : null}
                        </div>

                        <AttachmentGrid
                          attachments={rfqOwnerAttachments}
                          onDownload={downloadRfqOwnerAttachment}
                          downloadingS3Key={downloadingS3Key}
                          isDownloadDisabled={Boolean(downloadingS3Key) || isDownloadingAll}
                        />
                      </>
                    ) : null}

                        <div ref={termsSectionRef}>
                          <SectionTitle icon="fa-list-check" title="Terms" />
                         </div>

                    {!showTermsComments && termsRows.length === 0 ? (
                      <div className="txtdsply text-muted mb-3">No terms</div>
                    ) : !showTermsComments ? (
                      <div className="table-responsive mb-3">
                        <table className="table table-bordered table-sm mb-0">
                          <thead>
                            <tr style={{ backgroundColor: '#003366', color: '#fff' }}>
                              <th style={{ fontSize: '12px', width: '48px' }}>S.No</th>
                              <th style={{ fontSize: '12px', minWidth: '220px' }}>RFQ Term</th>
                              <th style={{ fontSize: '12px', minWidth: '220px' }}>Terms Response</th>
                            </tr>
                          </thead>
                          <tbody>
                            {termsRows.map((row, index) => (
                              <tr key={index}>
                                <td>{row.sno ?? index + 1}</td>
                                <td
                                  style={{
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word'
                                  }}
                                >
                                  {row.rfqTerm}
                                </td>
                                <td
                                  style={{
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word'
                                  }}
                                >
                                  {row.termsResponse}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    ) : supplierTerms.length === 0 ? (
                      <div className="txtdsply text-muted mb-3">No terms</div>
                    ) : (
                      <div className="table-responsive mb-3">
                        <table className="table table-bordered table-sm mb-0">
                          <thead>
                            <tr style={{ backgroundColor: '#003366', color: '#fff' }}>
                              <th style={{ fontSize: '12px', width: '48px' }}>S.No</th>
                              <th style={{ fontSize: '12px', minWidth: '200px' }}>RFQ Term</th>
                              <th style={{ fontSize: '12px', minWidth: '200px' }}>Terms Response</th>
                              <th style={{ fontSize: '12px', minWidth: '200px' }}>
                                Quote Response
                                {isPriceEditMode ? <span className="text-warning"> *</span> : null}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {supplierTerms.map((row, index) => (
                              <tr key={index}>
                                <td style={{ verticalAlign: 'top' }}>{row.sno ?? index + 1}</td>
                                <td
                                  style={{
                                    verticalAlign: 'top',
                                    whiteSpace: 'normal',
                                    wordBreak: 'break-word',
                                    paddingTop: '0.375rem'
                                  }}
                                >
                                  {row.rfqTerm || '-'}
                                </td>
                                <td
                                  style={{
                                    verticalAlign: 'top',
                                    whiteSpace: 'pre-wrap',
                                    wordBreak: 'break-word',
                                    paddingTop: '0.375rem'
                                  }}
                                >
                                  {row.termsResponse || '-'}
                                </td>
                                <td style={{ verticalAlign: 'top' }}>
                                  {isPriceEditMode ? (
                                    <>
                                      <textarea
                                        className={`form-control form-control-sm${
                                          submitErrors.terms[index] ? ' is-invalid' : ''
                                        }`}
                                        rows={2}
                                        placeholder="Enter comments..."
                                        value={row.comments}
                                        onChange={(e) =>
                                          handleTermsCommentChange(index, e.target.value)
                                        }
                                      />
                                      {submitErrors.terms[index] ? (
                                        <div className="invalid-feedback d-block">
                                          {submitErrors.terms[index]}
                                        </div>
                                      ) : null}
                                    </>
                                  ) : (
                                    <span
                                      style={{
                                        whiteSpace: 'pre-wrap',
                                        wordBreak: 'break-word',
                                        display: 'block',
                                        paddingTop: '0.375rem'
                                      }}
                                    >
                                      {row.comments || '-'}
                                    </span>
                                  )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}

                    <SectionTitle
                      icon="fa-boxes-stacked"
                      title="Materials"
                      actions={
                        materials.length > 0 ? (
                          <div className="d-inline-flex flex-wrap align-items-center">
                            <button
                              type="button"
                              className="btn btn-sm btn-outline-primary mr-2 mb-1"
                              onClick={handleExportSupplierQuote}
                              disabled={
                                isExportingQuote ||
                                isImportingQuote ||
                                isSubmitting
                              }
                              title="Download quote Excel (yellow = editable, do not change LineKey)"
                            >
                              {isExportingQuote ? (
                                <>
                                  <span
                                    className="spinner-border spinner-border-sm mr-1"
                                    role="status"
                                    aria-hidden="true"
                                  />
                                  Exporting...
                                </>
                              ) : (
                                <>
                                  <i className="fa-solid fa-file-excel mr-1" />
                                  Export
                                </>
                              )}
                            </button>
                            {canUpdateQuote ? (
                              <>
                                <input
                                  ref={quoteImportInputRef}
                                  type="file"
                                  accept=".xlsx,.xls,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.ms-excel"
                                  style={{ display: 'none' }}
                                  onChange={handleImportSupplierQuoteFile}
                                />
                                <button
                                  type="button"
                                  className="btn btn-sm btn-outline-secondary mb-1"
                                  onClick={() =>
                                    quoteImportInputRef.current?.click()
                                  }
                                  disabled={
                                    isExportingQuote ||
                                    isImportingQuote ||
                                    isSubmitting
                                  }
                                  title="Import Excel into the form (not saved until Submit)"
                                >
                                  {isImportingQuote ? (
                                    <>
                                      <span
                                        className="spinner-border spinner-border-sm mr-1"
                                        role="status"
                                        aria-hidden="true"
                                      />
                                      Importing...
                                    </>
                                  ) : (
                                    <>
                                      <i className="fa-solid fa-file-import mr-1" />
                                      Import
                                    </>
                                  )}
                                </button>
                              </>
                            ) : null}
                          </div>
                        ) : null
                      }
                    />

                    {materials.length === 0 ? (
                      <div className="txtdsply text-muted mb-3">No materials</div>
                    ) : (
                      <div className="table-responsive mb-3">
                        <table
                          className="table table-bordered table-sm mb-0"
                          style={{
                            minWidth: showMaterialPricing
                              ? showPriceMatchTargetColumn
                                ? '1440px'
                                : '1320px'
                              : '960px'
                          }}
                        >
                          <thead>
                            <tr style={{ backgroundColor: '#003366', color: '#fff' }}>
                              <th style={{ fontSize: '12px', width: '48px' }}>S.No</th>
                              <th style={{ fontSize: '12px', minWidth: '140px' }}>Material Code</th>
                              <th style={{ fontSize: '12px', minWidth: '220px' }}>Material Description</th>
                              <th style={{ fontSize: '12px', minWidth: '80px' }}>
                                HSN
                                {isPriceEditMode ? <span className="text-warning"> *</span> : null}
                              </th>
                              <th style={{ fontSize: '12px', minWidth: '64px' }}>UOM</th>
                              <th style={{ fontSize: '12px', minWidth: '72px' }}>Quantity</th>
                              {showMaterialPricing ? (
                                <>
                                  {showPriceMatchTargetColumn ? (
                                    <th style={{ fontSize: '12px', minWidth: '110px' }}>
                                      Target Price
                                    </th>
                                  ) : null}
                                  <th style={{ fontSize: '12px', minWidth: '110px' }}>
                                    Basic Price
                                    {isPriceEditMode ? <span className="text-warning"> *</span> : null}
                                  </th>
                                  <th style={{ fontSize: '12px', minWidth: '90px' }}>
                                    GST %
                                    {isPriceEditMode ? <span className="text-warning"> *</span> : null}
                                  </th>
                                  <th style={{ fontSize: '12px', minWidth: '100px' }}>
                                    GST Value
                                  </th>
                                  <th style={{ fontSize: '12px', minWidth: '100px' }}>
                                    Total Value
                                  </th>
                                  <th style={{ fontSize: '12px', minWidth: '180px' }}>
                                    Technical Response
                                    {isPriceEditMode ? <span className="text-warning"> *</span> : null}
                                  </th>
                                </>
                              ) : null}
                            </tr>
                          </thead>
                          <tbody>
                            {pricedMaterials.flatMap((row, index) => {
                              const material = row.material
                              const lineNo =
                                material.lineNo != null
                                  ? material.lineNo
                                  : material.sno ?? index + 1
                              const hasBoq = materialHasBoq(material)
                              const boqSource = getMaterialBoqItems(material)
                              const savedQuote = findMaterialQuote(
                                material,
                                index,
                                supplierQuote?.materialQuotes
                              )
                              const display = getMaterialDisplayValues(
                                material,
                                row.priceRow,
                                {
                                  basicPrice: row.basicPrice,
                                  gstValue: row.gstValue,
                                  totalValue: row.totalValue
                                },
                                isPriceEditMode
                              )

                              const materialTarget = getMaterialEffectiveTargetPrice(
                                material
                              )
                              const materialQuotedUnit = (() => {
                                if (hasBoq) {
                                  return Number.isFinite(Number(row.basicPrice))
                                    ? Number(row.basicPrice)
                                    : parseDecimalValue(material.basicPrice)
                                }
                                const edited = parseDecimalValue(
                                  row.priceRow.basicPrice
                                )
                                const saved = parseDecimalValue(
                                  savedQuote?.basicPrice ?? material.basicPrice
                                )
                                return Number.isFinite(edited)
                                  ? edited
                                  : saved
                              })()
                              const showMaterialTarget =
                                showPriceMatchTargetColumn &&
                                !hasBoq &&
                                shouldShowPriceMatchTarget(
                                  materialQuotedUnit,
                                  materialTarget
                                )
                              const pricingCols = showMaterialPricing ? (
                                <>
                                  {showPriceMatchTargetColumn ? (
                                    <td className="text-right align-middle">
                                      {showMaterialTarget ? (
                                        <div>
                                          <span className="fwt_600 text-primary">
                                            {formatAmount(materialTarget)}
                                          </span>
                                          {Number(
                                            material.supplierTargetPrice
                                          ) > 0 &&
                                          Number(
                                            material.allSuppliersTargetPrice
                                          ) > 0 &&
                                          Number(material.supplierTargetPrice) !==
                                            Number(
                                              material.allSuppliersTargetPrice
                                            ) ? (
                                            <div
                                              className="text-muted"
                                              style={{ fontSize: '10px' }}
                                            >
                                              All:{' '}
                                              {formatAmount(
                                                material.allSuppliersTargetPrice
                                              )}
                                            </div>
                                          ) : null}
                                        </div>
                                      ) : (
                                        <span className="text-muted">—</span>
                                      )}
                                    </td>
                                  ) : null}
                                  <td
                                    className={
                                      isPriceEditMode && !hasBoq
                                        ? undefined
                                        : 'text-right align-middle'
                                    }
                                  >
                                    {isPriceEditMode && !hasBoq ? (
                                      <>
                                        <input
                                          type="text"
                                          className={`form-control form-control-sm${
                                            submitErrors.materials[index]?.basicPrice
                                              ? ' is-invalid'
                                              : ''
                                          }`}
                                          inputMode="decimal"
                                          placeholder="0.00"
                                          value={row.priceRow.basicPrice}
                                          onChange={(e) =>
                                            handleMaterialPriceChange(
                                              index,
                                              'basicPrice',
                                              e.target.value
                                            )
                                          }
                                        />
                                        {submitErrors.materials[index]?.basicPrice ? (
                                          <div className="invalid-feedback d-block">
                                            {submitErrors.materials[index].basicPrice}
                                          </div>
                                        ) : null}
                                      </>
                                    ) : (
                                      <span className={hasBoq ? 'fwt_600' : undefined}>
                                        {formatAmount(display.basicPrice)}
                                      </span>
                                    )}
                                  </td>
                                  <td className={isPriceEditMode ? undefined : 'text-right align-middle'}>
                                    {isPriceEditMode ? (
                                      <>
                                        <input
                                          type="text"
                                          className={`form-control form-control-sm${
                                            submitErrors.materials[index]?.gstPercent
                                              ? ' is-invalid'
                                              : ''
                                          }`}
                                          inputMode="decimal"
                                          placeholder="0"
                                          value={row.priceRow.gstPercent}
                                          onChange={(e) =>
                                            handleMaterialPriceChange(
                                              index,
                                              'gstPercent',
                                              e.target.value
                                            )
                                          }
                                        />
                                        {submitErrors.materials[index]?.gstPercent ? (
                                          <div className="invalid-feedback d-block">
                                            {submitErrors.materials[index].gstPercent}
                                          </div>
                                        ) : null}
                                      </>
                                    ) : (
                                      formatAmount(display.gstPercent)
                                    )}
                                  </td>
                                  <td className="text-right align-middle">
                                    <span className={hasBoq ? 'fwt_600' : undefined}>
                                      {formatAmount(display.gstValue)}
                                    </span>
                                  </td>
                                  <td className="text-right align-middle fwt_600">
                                    {formatAmount(display.totalValue)}
                                  </td>
                                  <td>
                                    {isPriceEditMode ? (
                                      <>
                                        <textarea
                                          className={`form-control form-control-sm${
                                            submitErrors.materials[index]?.comments
                                              ? ' is-invalid'
                                              : ''
                                          }`}
                                          rows={2}
                                          placeholder="Enter comments..."
                                          value={row.priceRow.comments || ''}
                                          onChange={(e) =>
                                            handleMaterialPriceChange(
                                              index,
                                              'comments',
                                              e.target.value
                                            )
                                          }
                                        />
                                        {submitErrors.materials[index]?.comments ? (
                                          <div className="invalid-feedback d-block">
                                            {submitErrors.materials[index].comments}
                                          </div>
                                        ) : null}
                                      </>
                                    ) : (
                                      <span
                                        style={{
                                          whiteSpace: 'pre-wrap',
                                          wordBreak: 'break-word',
                                          display: 'block'
                                        }}
                                      >
                                        {display.technicalResponse}
                                      </span>
                                    )}
                                  </td>
                                </>
                              ) : null

                              const parentRow = (
                                <tr
                                  key={
                                    getMaterialPrId(material) ||
                                    `${material.materialCode || 'm'}-${index}`
                                  }
                                >
                                  <td>
                                    {lineNo}
                                    {hasBoq ? (
                                      <span
                                        className="badge badge-warning ml-1"
                                        style={{ fontSize: '9px' }}
                                      >
                                        BOQ
                                      </span>
                                    ) : null}
                                  </td>
                                  <td>{material.materialCode || '-'}</td>
                                  <td
                                    style={{
                                      whiteSpace: 'normal',
                                      wordBreak: 'break-word'
                                    }}
                                  >
                                    {material.materialDescription || '-'}
                                  </td>
                                  <td>
                                    {isPriceEditMode ? (
                                      <>
                                        <input
                                          type="text"
                                          className={`form-control form-control-sm${
                                            submitErrors.materials[index]?.hsn
                                              ? ' is-invalid'
                                              : ''
                                          }`}
                                          placeholder="HSN"
                                          value={row.priceRow.hsn || ''}
                                          onChange={(e) =>
                                            handleMaterialPriceChange(
                                              index,
                                              'hsn',
                                              e.target.value
                                            )
                                          }
                                        />
                                        {submitErrors.materials[index]?.hsn ? (
                                          <div className="invalid-feedback d-block">
                                            {submitErrors.materials[index].hsn}
                                          </div>
                                        ) : null}
                                      </>
                                    ) : (
                                      row.priceRow.hsn ||
                                      savedQuote?.hsn ||
                                      material.hsn ||
                                      material.hsnCode ||
                                      '-'
                                    )}
                                  </td>
                                  <td>{material.uom || '-'}</td>
                                  <td>{material.quantity ?? '-'}</td>
                                  {pricingCols}
                                </tr>
                              )

                              const boqRows = boqSource.map((boqItem, boqIndex) => {
                                const boqNo =
                                  boqItem.boqNo || `${lineNo}.${boqIndex + 1}`
                                const boqPriceRow =
                                  row.priceRow.boqPrices?.[boqIndex] || {
                                    basicPrice: ''
                                  }
                                const boqPriceError =
                                  submitErrors.materials[index]?.boqPrices?.[
                                    boqIndex
                                  ]
                                const savedBoqPrice = findBoqQuotePrice(
                                  boqItem,
                                  boqIndex,
                                  savedQuote
                                )
                                const boqUnitPrice = isPriceEditMode
                                  ? parseDecimalValue(boqPriceRow.basicPrice) || 0
                                  : parseDecimalValue(
                                      savedBoqPrice || boqItem.basicPrice
                                    ) || 0
                                const boqQty = parseFloat(boqItem.quantity) || 0
                                const boqLineBasic = boqUnitPrice * boqQty
                                const boqTarget = getBoqEffectiveTargetPrice(boqItem)
                                const boqQuotedForTarget = (() => {
                                  const edited = parseDecimalValue(
                                    boqPriceRow.basicPrice
                                  )
                                  const saved = parseDecimalValue(
                                    savedBoqPrice || boqItem.basicPrice
                                  )
                                  return Number.isFinite(edited)
                                    ? edited
                                    : saved
                                })()
                                const showBoqTarget =
                                  showPriceMatchTargetColumn &&
                                  shouldShowPriceMatchTarget(
                                    boqQuotedForTarget,
                                    boqTarget
                                  )
                                return (
                                  <tr
                                    key={`mat-${index}-boq-${boqItem.boq_id || boqItem._id || boqIndex}`}
                                    style={{ backgroundColor: '#fff8e6' }}
                                  >
                                    <td
                                      style={{
                                        fontWeight: 600,
                                        color: '#856404',
                                        paddingLeft: '14px'
                                      }}
                                    >
                                      {boqNo}
                                    </td>
                                    <td>{boqItem.materialCode || '-'}</td>
                                    <td
                                      style={{
                                        whiteSpace: 'normal',
                                        wordBreak: 'break-word'
                                      }}
                                    >
                                      {boqItem.materialDescription || '-'}
                                    </td>
                                    <td>
                                      {boqItem.hsn || boqItem.hsnCode || '-'}
                                    </td>
                                    <td>{boqItem.uom || '-'}</td>
                                    <td>{boqItem.quantity ?? '-'}</td>
                                    {showMaterialPricing ? (
                                      <>
                                        {showPriceMatchTargetColumn ? (
                                          <td className="text-right align-middle">
                                            {showBoqTarget ? (
                                              <div>
                                                <span className="fwt_600 text-primary">
                                                  {formatAmount(boqTarget)}
                                                </span>
                                                {Number(
                                                  boqItem.supplierTargetPrice
                                                ) > 0 &&
                                                Number(
                                                  boqItem.allSuppliersTargetPrice
                                                ) > 0 &&
                                                Number(
                                                  boqItem.supplierTargetPrice
                                                ) !==
                                                  Number(
                                                    boqItem.allSuppliersTargetPrice
                                                  ) ? (
                                                  <div
                                                    className="text-muted"
                                                    style={{ fontSize: '10px' }}
                                                  >
                                                    All:{' '}
                                                    {formatAmount(
                                                      boqItem.allSuppliersTargetPrice
                                                    )}
                                                  </div>
                                                ) : null}
                                              </div>
                                            ) : (
                                              <span className="text-muted">—</span>
                                            )}
                                          </td>
                                        ) : null}
                                        <td
                                          className={
                                            isPriceEditMode
                                              ? undefined
                                              : 'text-right align-middle'
                                          }
                                        >
                                          {isPriceEditMode ? (
                                            <>
                                              <input
                                                type="text"
                                                className={`form-control form-control-sm${
                                                  boqPriceError ? ' is-invalid' : ''
                                                }`}
                                                inputMode="decimal"
                                                placeholder="0.00"
                                                value={boqPriceRow.basicPrice}
                                                onChange={(e) =>
                                                  handleBoqBasicPriceChange(
                                                    index,
                                                    boqIndex,
                                                    e.target.value
                                                  )
                                                }
                                              />
                                              {boqPriceError ? (
                                                <div className="invalid-feedback d-block">
                                                  {boqPriceError}
                                                </div>
                                              ) : null}
                                            </>
                                          ) : (
                                            formatAmount(
                                              savedBoqPrice ||
                                                boqItem.basicPrice ||
                                                0
                                            )
                                          )}
                                        </td>
                                        <td className="text-center text-muted">—</td>
                                        <td className="text-center text-muted">—</td>
                                        <td className="text-right align-middle text-muted">
                                          {formatAmount(boqLineBasic)}
                                        </td>
                                        <td className="text-center text-muted">—</td>
                                      </>
                                    ) : null}
                                  </tr>
                                )
                              })

                              return [parentRow, ...boqRows]
                            })}
                            {showMaterialPricing ? (
                              <tr style={{ backgroundColor: '#f4f6f9' }}>
                                <td
                                  colSpan={showPriceMatchTargetColumn ? 7 : 6}
                                  className="text-right fwt_600 align-middle"
                                  style={{ fontSize: '13px' }}
                                >
                                  Gross Total
                                  {currency ? ` (${currency})` : ''}
                                </td>
                                <td className="text-right fwt_600 align-middle">
                                  {formatAmount(grossTotals.grossBasicPrice)}
                                </td>
                                <td />
                                <td className="text-right fwt_600 align-middle">
                                  {formatAmount(grossTotals.grossGstValue)}
                                </td>
                                <td
                                  className={`text-right fwt_600 align-middle${
                                    exceedsPreviousQuoteMax ? ' text-danger' : ''
                                  }`}
                                >
                                  {formatAmount(grossTotals.grossTotalValue)}
                                </td>
                                <td />
                              </tr>
                            ) : null}
                          </tbody>
                        </table>
                      </div>
                    )}

                    {isPriceMatchingMode &&
                    showMaterialPricing &&
                    showPriceMatchTargetColumn ? (
                      <div
                        className={`alert ${
                          exceedsPreviousQuoteMax ? 'alert-danger' : 'alert-info'
                        } mt-2 mb-3`}
                        role="alert"
                        style={{ fontSize: '13px' }}
                      >
                        <i
                          className={`fa-solid ${
                            exceedsPreviousQuoteMax
                              ? 'fa-circle-exclamation'
                              : 'fa-bullseye'
                          } mr-2`}
                        />
                        {hasRfqLevelPriceMatchTarget ? (
                          <>
                            Price Matching:{' '}
                            <strong>
                              {formatAmount(rfqLevelPriceMatchTarget)}
                              {currency ? ` ${currency}` : ''}
                            </strong>
                          </>
                        ) : (
                          <>Price matching is active for line / BOQ targets</>
                        )}
                        {Number.isFinite(previousQuoteMaxTotal) ? (
                          <>
                            . Max allowed Total Value:{' '}
                            <strong>
                              {formatAmount(previousQuoteMaxTotal)}
                              {currency ? ` ${currency}` : ''}
                            </strong>{' '}
                            (previous quote before matching). Current Total
                            Value:{' '}
                            <strong>
                              {formatAmount(grossTotals.grossTotalValue)}
                              {currency ? ` ${currency}` : ''}
                            </strong>
                            . Per-line targets above your quote are listed under
                            Target Price.
                          </>
                        ) : (
                          <>
                            . Per-line / BOQ targets above your quote are listed
                            in the Materials table.
                          </>
                        )}
                        {(previousQuoteRecord || quoteAfterTargetRecord) && (
                          <div className="mt-2 mb-0">
                            {previousQuoteRecord ? (
                              <div>
                                Previous quote (before target):{' '}
                                <strong>
                                  {formatAmount(
                                    previousQuoteRecord.grossTotalValue
                                  )}
                                  {currency ? ` ${currency}` : ''}
                                </strong>
                                {previousQuoteRecord.quoteSubmittedAt ? (
                                  <span className="text-muted ml-1">
                                    (
                                    {formatDueDateTime(
                                      previousQuoteRecord.quoteSubmittedAt
                                    )}
                                    )
                                  </span>
                                ) : null}
                              </div>
                            ) : null}
                            {quoteAfterTargetRecord ? (
                              <div>
                                Latest quote (after target):{' '}
                                <strong>
                                  {formatAmount(
                                    quoteAfterTargetRecord.grossTotalValue
                                  )}
                                  {currency ? ` ${currency}` : ''}
                                </strong>
                                {quoteAfterTargetRecord.quoteSubmittedAt ? (
                                  <span className="text-muted ml-1">
                                    (
                                    {formatDueDateTime(
                                      quoteAfterTargetRecord.quoteSubmittedAt
                                    )}
                                    )
                                  </span>
                                ) : null}
                              </div>
                            ) : null}
                            {!isPriceEditMode ? (
                              <div>
                                Current live quote:{' '}
                                <strong>
                                  {formatAmount(grossTotals.grossTotalValue)}
                                  {currency ? ` ${currency}` : ''}
                                </strong>
                              </div>
                            ) : null}
                          </div>
                        )}
                        {exceedsPreviousQuoteMax ? (
                          <span className="d-block mt-1">
                            Current Total Value exceeds the previous quotation.
                            Please reduce prices before submitting.
                          </span>
                        ) : null}
                      </div>
                    ) : null}

                    {(isPriceEditMode || savedSupplierAttachments.length > 0) && (
                      <>
                        <div className="row mb-2 mt-3 align-items-center">
                          <div className="col-12 col-md">
                            <h5 className="text-primary border-bottom pb-2 mb-0">
                              <i className="fa-solid fa-paperclip mr-2" />
                              Your Quotation Attachments
                              {supplierAttachmentCount > 0 ? (
                                <span
                                  className="badge badge-info ml-2"
                                  style={{ fontSize: '11px', verticalAlign: 'middle' }}
                                >
                                  {supplierAttachmentCount}{' '}
                                  {supplierAttachmentCount === 1 ? 'file' : 'files'}
                                </span>
                              ) : null}
                            </h5>
                          </div>
                        </div>

                        {isPriceEditMode ? (
                          <div className="mb-3">
                            <p className="text-muted mb-2" style={{ fontSize: '13px' }}>
                              Upload your quotation reference documents (max {MAX_SUPPLIER_ATTACHMENTS} files).
                              Allowed formats: {formatAllowedFormatsList()}.
                            </p>
                            <input
                              ref={supplierAttachmentInputRef}
                              type="file"
                              multiple
                              accept=".jpeg,.jpg,.png,.pdf,.csv,.xlsx,.ppt"
                              style={{ display: 'none' }}
                              onChange={handleSupplierAttachmentFileSelect}
                              disabled={isSubmitting || isUploadingSupplierAttachments}
                            />
                            <button
                              type="button"
                              className="btn btn-blue btn-sm mb-3"
                              onClick={() => supplierAttachmentInputRef.current?.click()}
                              disabled={
                                isSubmitting ||
                                isUploadingSupplierAttachments ||
                                supplierAttachmentCount >= MAX_SUPPLIER_ATTACHMENTS
                              }
                            >
                              <i className="fa-solid fa-paperclip mr-1" />
                              Choose Files
                            </button>

                            {isUploadingSupplierAttachments ? (
                              <div className="text-muted mb-3" style={{ fontSize: '13px' }}>
                                <span className="mr-2">
                                  <PulseLoader color="#1199B2" size={6} />
                                </span>
                                Uploading attachments...
                              </div>
                            ) : null}

                            <AttachmentGrid
                              attachments={visibleSupplierAttachments}
                              pendingFiles={pendingSupplierFiles}
                              onDownload={downloadSupplierAttachment}
                              downloadingS3Key={downloadingS3Key}
                              isDownloadDisabled={
                                isSubmitting || isUploadingSupplierAttachments
                              }
                              onRemove={markSupplierAttachmentForRemoval}
                              onRemovePending={removePendingSupplierFile}
                              emptyMessage=' Choose Files to upload quotation reference documents.'
                              emptyIcon="fa-solid fa-file-circle-plus"
                            />
                          </div>
                        ) : (
                          <AttachmentGrid
                            attachments={savedSupplierAttachments}
                            onDownload={downloadSupplierAttachment}
                            downloadingS3Key={downloadingS3Key}
                            isDownloadDisabled={Boolean(downloadingS3Key)}
                            emptyMessage="No quotation attachments uploaded"
                          />
                        )}
                      </>
                    )}

                    {(hasExistingQuote || showSubmitSuccess) && !isPriceEditMode ? (
                      <div className="alert alert-success mt-4 mb-0 text-center" role="alert">
                        <i className="fa-solid fa-circle-check mr-2" />
                        {submitSuccessMessage ||
                          'Thank you for your response. Your quotation has been submitted successfully.'}
                      </div>
                    ) : null}

                    {materials.length > 0 && canUpdateQuote ? (
                      <div className="mt-4 pt-3 border-top">
                        {submitErrors.formMessage ? (
                          <div className="alert alert-danger mb-3" role="alert">
                            {submitErrors.formMessage}
                          </div>
                        ) : null}
                        <div className="d-flex justify-content-end flex-wrap">
                        {!isPriceEditMode ? (
                          <button
                            type="button"
                            className="btn btn-blue"
                            onClick={handleStartPriceUpdate}
                          >
                            <i className="fa-solid fa-pen-to-square mr-1" />
                            Update Price
                          </button>
                        ) : (
                          <>
                            <button
                              type="button"
                              className="btn btn-outline-secondary mr-2"
                              onClick={handleCancelPriceUpdate}
                              disabled={isSubmitting || isUploadingSupplierAttachments}
                            >
                              Cancel
                            </button>
                            <button
                              type="button"
                              className="btn btn-blue"
                              onClick={handleSubmitPrices}
                              disabled={
                                isSubmitting ||
                                isUploadingSupplierAttachments ||
                                exceedsPreviousQuoteMax
                              }
                            >
                              {isSubmitting || isUploadingSupplierAttachments ? (
                                <>
                                  <span
                                    className="spinner-border spinner-border-sm mr-1"
                                    role="status"
                                    aria-hidden="true"
                                  />
                                  Submitting...
                                </>
                              ) : (
                                <>
                                  <i className="fa-solid fa-paper-plane mr-1" />
                                  Submit
                                </>
                              )}
                            </button>
                          </>
                        )}
                        </div>
                      </div>
                    ) : null}

                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </PublicPageShell>
  )
}

export default RfqSuppliers
